<!-- State Inspector Panel -->
<!-- Header -->
<div class="bg-surface-container p-4 bg-surface">
  <div class="text-lg font-semibold">🔍 State Inspector</div>
  <div class="text-xs-variant">Real-time Signal States</div>
  <!-- Cache diagnostic shortcut -->
  <button
    class="mt-2 w-full text-[10px] font-mono px-2 py-1 rounded bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-200 transition-colors text-left"
    (click)="dumpCacheKeysToConsole()">
    📦 Dump Cache Keys → console
  </button>
</div>
<div class="space-y-3 px-4 relative">
  <!-- ========== DEVICE STATE ========== -->
  <div class="space-y-3 bg-surface rounded-lg border border-outline-variant select-none overflow-hidden"
    [class.pb-4]="deviceExpanded()">
    <div
      class="flex items-center bg-surface clickable justify-between text-lg font-semibold py-4 px-3 cursor-pointer"
      [class.border-b]="deviceExpanded()" (click)="toggleDeviceExpanded()">
      <span>📱 Device State</span>
      <button class="p-2 hover:bg-surface-variant rounded transition-colors">
        @if (deviceExpanded()) {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
        } @else {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
        }
      </button>
    </div>

    <!-- Collapsible Content -->
    @if (deviceExpanded()) {
    <div class="space-y-3 pb-4 px-3">
      <!-- Screen & Device -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">Screen & Device</h4>

        <div class="space-y-1 text-xs">
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Screen:</span>
            <span class="font-mono">{{ device.screenWidth() }} × {{ device.screenHeight() }}</span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">Breakpoint:</span>
            <span class="font-medium">{{ device.breakpoint() }}</span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">Device:</span>
            <span class="font-medium capitalize">{{ device.deviceType() }}</span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">Orientation:</span>
            <span class="font-medium capitalize">{{ device.orientation() }}</span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">Pixel Ratio:</span>
            <span class="font-mono">{{ device.devicePixelRatio() }}x</span>
          </div>

          <div class="flex gap-2 flex-wrap">
            @if (device.isMobile()) {
            <span class="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-[10px]">📱 Mobile</span>
            }
            @if (device.isTablet()) {
            <span class="px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full text-[10px]">📱 Tablet</span>
            }
            @if (device.isDesktop()) {
            <span class="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-[10px]">🖥️ Desktop</span>
            }
            @if (device.isTouchDevice()) {
            <span class="px-2 py-0.5 bg-orange-100 text-orange-800 rounded-full text-[10px]">👆 Touch</span>
            }
            @if (device.isRetinaDisplay()) {
            <span class="px-2 py-0.5 bg-pink-100 text-pink-800 rounded-full text-[10px]">✨ Retina</span>
            }
            @if (device.isPortrait()) {
            <span class="px-2 py-0.5 bg-indigo-100 text-indigo-800 rounded-full text-[10px]">📱 Portrait</span>
            }
            @if (device.isLandscape()) {
            <span class="px-2 py-0.5 bg-teal-100 text-teal-800 rounded-full text-[10px]">📱 Landscape</span>
            }
          </div>
        </div>
      </div>

      <!-- Browser & Platform -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">Browser & Platform</h4>

        <div class="space-y-1 text-xs">
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Browser:</span>
            <span class="font-medium capitalize">{{ device.browser() }}</span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">Platform:</span>
            <span class="font-mono text-[10px]">{{ device.platform() }}</span>
          </div>

          <div class="flex items-center gap-2">
            <div class="w-2 h-2 rounded-full" [class.bg-success]="device.isOnline()"
              [class.bg-danger]="!device.isOnline()"></div>
            <span>{{ device.isOnline() ? 'Online' : 'Offline' }}</span>
          </div>
        </div>
      </div>

      <!-- Page State -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">Page State</h4>

        <div class="space-y-1 text-xs">
          <div class="flex items-center gap-2">
            <div class="w-2 h-2 rounded-full" [class.bg-success]="device.isPageVisible()"
              [class.bg-gray-400]="!device.isPageVisible()"></div>
            <span>Visible: {{ device.isPageVisible() }}</span>
          </div>

          <div class="flex items-center gap-2">
            <div class="w-2 h-2 rounded-full" [class.bg-success]="device.isPageFocused()"
              [class.bg-gray-400]="!device.isPageFocused()"></div>
            <span>Focused: {{ device.isPageFocused() }}</span>
          </div>

          <div class="flex items-center gap-2">
            <div class="w-2 h-2 rounded-full" [class.bg-success]="device.isFullscreen()"
              [class.bg-gray-400]="!device.isFullscreen()"></div>
            <span>Fullscreen: {{ device.isFullscreen() }}</span>
          </div>

          <div class="flex items-center gap-2">
            <div class="w-2 h-2 rounded-full" [class.bg-yellow-500]="device.isPrintMode()"
              [class.bg-gray-400]="!device.isPrintMode()"></div>
            <span>Print Mode: {{ device.isPrintMode() }}</span>
          </div>
        </div>
      </div>

      <!-- Network -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">Network</h4>

        <div class="space-y-1 text-xs">
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Type:</span>
            <span class="font-medium capitalize">{{ device.connectionType() }}</span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">Effective:</span>
            <span class="font-medium">{{ device.effectiveConnectionType() }}</span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">Speed:</span>
            <span class="font-mono">{{ device.networkInfo().downlink }} Mbps</span>
          </div>

          <div class="flex gap-2 flex-wrap">
            @if (device.isSlowConnection()) {
            <span class="px-2 py-0.5 bg-red-100 text-red-800 rounded-full text-[10px]">🐌 Slow Connection</span>
            }
            @if (device.saveData()) {
            <span class="px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full text-[10px]">💾 Save Data</span>
            }
          </div>
        </div>
      </div>

      <!-- Mobile Specific -->
      @if (device.isMobile() || device.isTablet()) {
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">Mobile</h4>

        <div class="space-y-1 text-xs">
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Safe Insets:</span>
            <span class="font-mono text-[10px]">
              T:{{ device.safeAreaInsets().top }}
              R:{{ device.safeAreaInsets().right }}
              B:{{ device.safeAreaInsets().bottom }}
              L:{{ device.safeAreaInsets().left }}
            </span>
          </div>

          <div class="flex items-center gap-2">
            <div class="w-2 h-2 rounded-full" [class.bg-yellow-500]="device.isKeyboardVisible()"
              [class.bg-gray-400]="!device.isKeyboardVisible()"></div>
            <span>Keyboard: {{ device.isKeyboardVisible() ? 'Visible (' + device.keyboardHeight() + 'px)' : 'Hidden'
              }}</span>
          </div>
        </div>
      </div>
      }

      <!-- PWA -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">PWA</h4>

        <div class="space-y-1 text-xs">
          <div class="flex items-center gap-2">
            <div class="w-2 h-2 rounded-full" [class.bg-success]="device.isPWAInstalled()"
              [class.bg-gray-400]="!device.isPWAInstalled()"></div>
            <span>Installed: {{ device.isPWAInstalled() }}</span>
          </div>

          <div class="flex items-center gap-2">
            <div class="w-2 h-2 rounded-full" [class.bg-blue-500]="device.isPWAInstallable()"
              [class.bg-gray-400]="!device.isPWAInstallable()"></div>
            <span>Installable: {{ device.isPWAInstallable() }}</span>
          </div>

          <div class="flex items-center gap-2">
            <div class="w-2 h-2 rounded-full" [class.bg-orange-500]="device.showPWAPrompt()"
              [class.bg-gray-400]="!device.showPWAPrompt()"></div>
            <span>Show Prompt: {{ device.showPWAPrompt() }}</span>
          </div>
        </div>
      </div>

      <!-- Locale & i18n -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">Locale & i18n</h4>

        <div class="space-y-1 text-xs">
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Locale:</span>
            <span class="font-mono text-[10px]">{{ device.locale() }}</span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">Language:</span>
            <span class="font-medium">{{ device.language() }}</span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">Timezone:</span>
            <span class="font-mono text-[10px]">{{ device.timezone() }}</span>
          </div>
        </div>
      </div>

      <!-- Scroll -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">Scroll</h4>

        <div class="space-y-1 text-xs">
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Position:</span>
            <span class="font-mono">{{ device.scrollY() }}px</span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">Direction:</span>
            <span class="font-medium">{{ device.scrollDirection() }}</span>
          </div>

          <div class="flex items-center gap-2">
            <div class="w-2 h-2 rounded-full" [class.bg-blue-500]="device.isScrolled()"
              [class.bg-gray-400]="!device.isScrolled()"></div>
            <span>Scrolled: {{ device.isScrolled() }}</span>
          </div>
        </div>
      </div>

      <!-- User Activity -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">User Activity</h4>

        <div class="space-y-1 text-xs">
          <div class="flex items-center gap-2">
            <div class="w-2 h-2 rounded-full" [class.bg-red-500]="device.isIdle()"
              [class.bg-success]="!device.isIdle()"></div>
            <span>{{ device.isIdle() ? 'Idle' : 'Active' }}</span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">Idle Duration:</span>
            <span class="font-mono">{{ (device.idleDuration() / 1000).toFixed(0) }}s</span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">Last Interaction:</span>
            <span class="font-mono text-[10px]">{{ device.lastInteractionTime() | date:'HH:mm:ss' }}</span>
          </div>
        </div>
      </div>

      <!-- Performance -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">Performance</h4>

        <div class="space-y-1 text-xs">
          <div class="flex justify-between">
            <span class="text-on-surface-variant">FPS:</span>
            <span class="font-mono" [class.text-success]="device.fps() >= 55"
              [class.text-yellow-600]="device.fps() >= 30 && device.fps() < 55"
              [class.text-red-600]="device.fps() < 30">
              {{ device.fps() }}
            </span>
          </div>

          @if (device.memoryUsage()) {
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Memory:</span>
            <span class="font-mono">{{ device.memoryUsage() }} MB</span>
          </div>
          }
        </div>
      </div>

      <!-- Permissions -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">Permissions</h4>

        <div class="space-y-1 text-xs">
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Notifications:</span>
            <span class="font-medium" [class.text-success]="device.permissions().notifications === 'granted'"
              [class.text-red-600]="device.permissions().notifications === 'denied'">
              {{ device.permissions().notifications }}
            </span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">Geolocation:</span>
            <span class="font-medium" [class.text-success]="device.permissions().geolocation === 'granted'"
              [class.text-red-600]="device.permissions().geolocation === 'denied'">
              {{ device.permissions().geolocation }}
            </span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">Camera:</span>
            <span class="font-medium" [class.text-success]="device.permissions().camera === 'granted'"
              [class.text-red-600]="device.permissions().camera === 'denied'">
              {{ device.permissions().camera }}
            </span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">Microphone:</span>
            <span class="font-medium" [class.text-success]="device.permissions().microphone === 'granted'"
              [class.text-red-600]="device.permissions().microphone === 'denied'">
              {{ device.permissions().microphone }}
            </span>
          </div>
        </div>
      </div>

      <!-- Storage -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">Storage</h4>

        <div class="space-y-1 text-xs">
          <div class="flex items-center gap-2">
            <div class="w-2 h-2 rounded-full" [class.bg-success]="device.storageInfo().hasLocalStorage"
              [class.bg-red-500]="!device.storageInfo().hasLocalStorage"></div>
            <span>LocalStorage: {{ device.storageInfo().hasLocalStorage }}</span>
          </div>

          <div class="flex items-center gap-2">
            <div class="w-2 h-2 rounded-full" [class.bg-success]="device.storageInfo().hasSessionStorage"
              [class.bg-red-500]="!device.storageInfo().hasSessionStorage"></div>
            <span>SessionStorage: {{ device.storageInfo().hasSessionStorage }}</span>
          </div>

          <div class="flex items-center gap-2">
            <div class="w-2 h-2 rounded-full" [class.bg-success]="device.storageInfo().hasCookies"
              [class.bg-red-500]="!device.storageInfo().hasCookies"></div>
            <span>Cookies: {{ device.storageInfo().hasCookies }}</span>
          </div>

          @if (device.storageInfo().quota) {
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Quota:</span>
            <span class="font-mono text-[10px]">
              {{ device.storageInfo().quota?.used }} / {{ device.storageInfo().quota?.total }} MB
            </span>
          </div>
          }
        </div>
      </div>

      <!-- System Preferences -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">System</h4>

        <div class="space-y-1 text-xs">
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Color Scheme:</span>
            <span class="font-medium capitalize">{{ device.systemColorScheme() }}</span>
          </div>

          <div class="flex gap-2 flex-wrap">
            @if (device.prefersReducedMotion()) {
            <span class="px-2 py-0.5 bg-gray-100 text-gray-800 rounded-full text-[10px]">🎬 Reduced Motion</span>
            }
            @if (device.prefersHighContrast()) {
            <span class="px-2 py-0.5 bg-gray-100 text-gray-800 rounded-full text-[10px]">🎨 High Contrast</span>
            }
          </div>
        </div>
      </div>

      <!-- UI Preferences -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">Preferences</h4>

        <div class="space-y-1 text-xs">
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Theme:</span>
            <span class="font-medium capitalize">{{ device.theme() }}</span>
          </div>
        </div>
      </div>

      <!-- Raw Data (Collapsible) -->
      <details class="space-y-2">
        <summary class="text-xs font-medium-variant cursor-pointer">
          Raw Data (All 56 Properties)
        </summary>
        <pre class="p-2 bg-surface rounded text-[10px] overflow-x-auto">{{
          device.data() | json
        }}</pre>
      </details>
    </div>
    }
  </div>

  <!-- ========== ACTION BAR STATE ========== -->
  <div class="space-y-3 bg-surface rounded-lg border border-outline-variant select-none overflow-hidden"
    [class.pb-4]="actionBarExpanded()">
    <div
      class="flex items-center bg-surface clickable justify-between text-lg font-semibold py-4 px-3 cursor-pointer"
      [class.border-b]="actionBarExpanded()" (click)="toggleActionBarExpanded()">
      <span>🎯 Action Bar State</span>
      <button class="p-2 hover:bg-surface-variant rounded transition-colors">
        @if (actionBarExpanded()) {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
        } @else {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
        }
      </button>
    </div>

    <!-- Collapsible Content -->
    @if (actionBarExpanded()) {
    <div class="space-y-3 pb-4 px-3">
      <!-- Visibility States -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">Visibility States</h4>

        <div class="space-y-1 text-xs">
          <div class="flex justify-between">
            <span class="text-on-surface-variant">User Visible:</span>
            <span class="font-medium">{{ actionBar.getUserVisible() }}</span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">Hidden by Side Menu:</span>
            <span class="font-medium">{{ actionBar.hiddenBySideMenu() }}</span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">Actual Visible:</span>
            <span class="font-medium font-bold" [class.text-success]="actionBar.visible()" [class.text-red-600]="!actionBar.visible()">
              {{ actionBar.visible() }}
            </span>
          </div>

          <div class="flex gap-2 flex-wrap mt-2">
            @if (actionBar.visible()) {
            <span class="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-[10px]">✓ Visible</span>
            } @else {
            <span class="px-2 py-0.5 bg-red-100 text-red-800 rounded-full text-[10px]">✗ Hidden</span>
            }
            @if (actionBar.hiddenBySideMenu()) {
            <span class="px-2 py-0.5 bg-orange-100 text-orange-800 rounded-full text-[10px]">🚫 Auto-hidden</span>
            }
          </div>
        </div>
      </div>

      <!-- Configuration -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">Configuration</h4>

        <div class="space-y-1 text-xs">
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Color:</span>
            <span class="font-medium capitalize">{{ actionBar.color() }}</span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">Min Width:</span>
            <span class="font-mono">{{ actionBar.minWidth() }}px</span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">Has Content:</span>
            <span class="font-medium">{{ actionBar.content() !== null }}</span>
          </div>
        </div>
      </div>

      <!-- State Status -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">State Status</h4>

        <div class="space-y-1 text-xs">
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Initialized:</span>
            <span class="font-medium">{{ actionBar.initialized() }}</span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">Ready:</span>
            <span class="font-medium">{{ actionBar.isReady() }}</span>
          </div>
        </div>
      </div>

      <!-- Full State (Collapsed by default) -->
      <details class="space-y-2">
        <summary class="text-xs font-medium-variant cursor-pointer hover:text-on-surface">
          Full State Data
        </summary>
        <pre class="p-2 bg-surface rounded text-[10px] overflow-x-auto">{{
          actionBar.data() | json
        }}</pre>
      </details>
    </div>
    }
  </div>

  <!-- ========== ROUTER STATE ========== -->
  <div class="space-y-3 bg-surface rounded-lg border border-outline-variant select-none overflow-hidden"
    [class.pb-4]="routerExpanded()">
    <div
      class="flex items-center bg-surface clickable justify-between text-lg font-semibold py-4 px-3 cursor-pointer"
      [class.border-b]="routerExpanded()" (click)="toggleRouterExpanded()">
      <span>🧭 Router State</span>
      <button class="p-2 hover:bg-surface-variant rounded transition-colors">
        @if (routerExpanded()) {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
        } @else {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
        }
      </button>
    </div>

    <!-- Collapsible Content -->
    @if (routerExpanded()) {
    <div class="space-y-3 pb-4 px-3">
      <!-- Current Route -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">Current Route</h4>

        <div class="space-y-1 text-xs">
          <div class="flex flex-col gap-1">
            <span class="text-on-surface-variant">URL:</span>
            <span class="font-mono text-[10px] break-all">{{ router.currentUrl() }}</span>
          </div>

          <div class="flex flex-col gap-1">
            <span class="text-on-surface-variant">Path:</span>
            <span class="font-mono text-[10px]">{{ router.currentPath() }}</span>
          </div>

          @if (router.routeTitle()) {
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Title:</span>
            <span class="font-medium">{{ router.routeTitle() }}</span>
          </div>
          }

          <div class="flex justify-between">
            <span class="text-on-surface-variant">Depth:</span>
            <span class="font-mono">{{ router.routeDepth() }}</span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">Nav ID:</span>
            <span class="font-mono">{{ router.navigationId() }}</span>
          </div>
        </div>
      </div>

      <!-- Route Parameters -->
      @if (router.hasRouteParams()) {
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">Route Params</h4>

        <div class="space-y-1">
          @for (param of router.routeParams() | keyvalue; track param.key) {
          <div class="flex justify-between text-xs">
            <span class="text-on-surface-variant">{{ param.key }}:</span>
            <span class="font-mono text-[10px]">{{ param.value }}</span>
          </div>
          }
        </div>
      </div>
      }

      <!-- Query Parameters -->
      @if (router.hasQueryParams()) {
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">Query Params</h4>

        <div class="space-y-1">
          @for (param of router.queryParams() | keyvalue; track param.key) {
          <div class="flex justify-between text-xs">
            <span class="text-on-surface-variant">{{ param.key }}:</span>
            <span class="font-mono text-[10px]">{{ param.value }}</span>
          </div>
          }
        </div>
      </div>
      }

      <!-- Fragment -->
      @if (router.hasFragment()) {
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">Fragment</h4>

        <div class="text-xs">
          <span class="font-mono text-[10px]">#{{ router.fragment() }}</span>
        </div>
      </div>
      }

      <!-- Navigation State -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">Navigation</h4>

        <div class="space-y-1 text-xs">
          <div class="flex items-center gap-2">
            <div class="w-2 h-2 rounded-full" [class.bg-yellow-500]="router.isNavigating()"
              [class.bg-gray-400]="!router.isNavigating()"></div>
            <span>Navigating: {{ router.isNavigating() }}</span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">Type:</span>
            <span class="font-medium capitalize">{{ router.navigationType() }}</span>
          </div>

          @if (router.lastNavigationDuration()) {
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Last Duration:</span>
            <span class="font-mono">{{ router.lastNavigationDuration() }}ms</span>
          </div>
          }

          <div class="flex items-center gap-2">
            <div class="w-2 h-2 rounded-full" [class.bg-success]="router.canGoBack()"
              [class.bg-gray-400]="!router.canGoBack()"></div>
            <span>Can Go Back: {{ router.canGoBack() }}</span>
          </div>
        </div>
      </div>

      <!-- Previous URL -->
      @if (router.previousUrl()) {
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">Previous URL</h4>

        <div class="text-xs">
          <span class="font-mono text-[10px] break-all">{{ router.previousUrl() }}</span>
        </div>
      </div>
      }

      <!-- Navigation History -->
      @if (router.historyCount() > 0) {
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">
          History ({{ router.historyCount() }})
        </h4>

        <div class="space-y-1 max-h-32 overflow-y-auto">
          @for (url of router.navigationHistory(); track $index) {
          <div class="p-1 bg-surface rounded text-[10px] font-mono break-all">
            {{ url }}
          </div>
          }
        </div>
      </div>
      }

      <!-- Breadcrumbs -->
      @if (router.breadcrumbCount() > 0) {
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">
          Breadcrumbs ({{ router.breadcrumbCount() }})
        </h4>

        <div class="space-y-1">
          @for (crumb of router.breadcrumbs(); track crumb.path) {
          <div class="p-2 bg-surface rounded">
            <div class="text-xs font-medium">{{ crumb.label }}</div>
            <div class="text-[10px] font-mono-variant">{{ crumb.path }}</div>
          </div>
          }
        </div>
      </div>
      }

      <!-- Raw Data (Collapsible) -->
      <details class="space-y-2">
        <summary class="text-xs font-medium-variant cursor-pointer">
          Raw Data
        </summary>
        <pre class="p-2 bg-surface rounded text-[10px] overflow-x-auto">{{
          router.data() | json
        }}</pre>
      </details>
    </div>
    }
  </div>

  <!-- ========== ACCOUNT STATE ========== -->
  <div class="space-y-3 bg-surface rounded-lg border border-outline-variant select-none overflow-hidden"
    [class.pb-4]="accountExpanded()">
    <div
      class="flex items-center bg-surface clickable justify-between text-lg font-semibold px-3 py-4 cursor-pointer"
      [class.border-b]="accountExpanded()" (click)="toggleAccountExpanded()">
      <span>👤 Account State (Global)</span>
      <button class="p-2 hover:bg-surface-variant rounded transition-colors">
        @if (accountExpanded()) {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
        } @else {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
        }
      </button>
    </div>

    <!-- Collapsible Content -->
    @if (accountExpanded()) {
    <div class="space-y-3 pb-4 px-3">
      <!-- Status Indicators -->
      <app-state-status-indicators [initialized]="account.initialized()" [loading]="account.loading()"
        [isReady]="account.isReady()" [hasData]="account.hasData()" [hasActiveTeam]="account.hasActiveTeam()"
        [error]="account.error()" />

      <!-- State Section - All Public Signals -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">📊 State (All Public Signals)</h4>

        <!-- Basic State Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Basic State</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">initialized:</span>
              <span class="font-medium" [class.text-success]="account.initialized()"
                [class.text-gray-600]="!account.initialized()">
                {{ account.initialized() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">loading:</span>
              <span class="font-medium" [class.text-yellow-600]="account.loading()"
                [class.text-gray-600]="!account.loading()">
                {{ account.loading() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">error:</span>
              <span class="font-mono text-[9px]" [class.text-red-600]="account.error()">
                {{ account.error() || 'null' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">lastUpdated:</span>
              <span class="font-mono text-[9px]">
                {{ account.lastUpdated() ? (account.lastUpdated() | date:'short') : 'null' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">email:</span>
              <span class="font-mono text-[9px]">{{ account.email() || 'null' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">firstName:</span>
              <span class="font-mono text-[9px]">{{ account.firstName() || 'null' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">lastName:</span>
              <span class="font-mono text-[9px]">{{ account.lastName() || 'null' }}</span>
            </div>
          </div>
        </div>

        <!-- Computed State Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Computed State</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">isReady:</span>
              <span class="font-medium" [class.text-success]="account.isReady()"
                [class.text-gray-600]="!account.isReady()">
                {{ account.isReady() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">hasData:</span>
              <span class="font-medium" [class.text-success]="account.hasData()"
                [class.text-gray-600]="!account.hasData()">
                {{ account.hasData() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">fullName:</span>
              <span class="font-mono text-[9px]">{{ account.fullName() || 'null' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">accountEmail:</span>
              <span class="font-mono text-[9px]">{{ account.accountEmail() || 'null' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">id:</span>
              <span class="font-mono text-[9px]">{{ account.id() || 'null' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">uid:</span>
              <span class="font-mono text-[9px]">{{ account.uid() || 'null' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">isBlumeUser:</span>
              <span class="font-medium" [class.text-purple-600]="account.isBlumeUser()"
                [class.text-gray-600]="!account.isBlumeUser()">
                {{ account.isBlumeUser() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">hasStudentIds:</span>
              <span class="font-medium" [class.text-success]="account.hasStudentIds()"
                [class.text-gray-600]="!account.hasStudentIds()">
                {{ account.hasStudentIds() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">studentIdsCount:</span>
              <span class="font-medium">{{ account.studentIdsCount() }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">studentIds:</span>
              <span class="font-mono text-[9px]">{{ account.studentIds().length > 0 ? '[' + account.studentIds().length + '
                ids]' : '[]' }}</span>
            </div>
          </div>
        </div>

        <!-- Partners Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Partners State</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">partnersLoading:</span>
              <span class="font-medium" [class.text-yellow-600]="account.partnersLoading()"
                [class.text-gray-600]="!account.partnersLoading()">
                {{ account.partnersLoading() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">partnersError:</span>
              <span class="font-mono text-[9px]" [class.text-red-600]="account.partnersError()">
                {{ account.partnersError() || 'null' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">hasPartners:</span>
              <span class="font-medium" [class.text-success]="account.hasPartners()"
                [class.text-gray-600]="!account.hasPartners()">
                {{ account.hasPartners() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">partnerCount:</span>
              <span class="font-medium">{{ account.partnerCount() }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">hasPartnerData:</span>
              <span class="font-medium" [class.text-success]="account.hasPartnerData()"
                [class.text-gray-600]="!account.hasPartnerData()">
                {{ account.hasPartnerData() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">partnerIds:</span>
              <span class="font-mono text-[9px]">{{ account.partnerIds().length > 0 ? '[' + account.partnerIds().length
                + ' ids]' : '[]' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">partners:</span>
              <span class="font-mono text-[9px]">{{ account.partners().length > 0 ? '[' + account.partners().length + '
                partners]' : '[]' }}</span>
            </div>
          </div>
        </div>

        <!-- Invites Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Invites State</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">invitesLoading:</span>
              <span class="font-medium" [class.text-yellow-600]="account.invitesLoading()"
                [class.text-gray-600]="!account.invitesLoading()">
                {{ account.invitesLoading() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">invitesError:</span>
              <span class="font-mono text-[9px]" [class.text-red-600]="account.invitesError()">
                {{ account.invitesError() || 'null' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">hasInvites:</span>
              <span class="font-medium" [class.text-success]="account.hasInvites()"
                [class.text-gray-600]="!account.hasInvites()">
                {{ account.hasInvites() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">inviteCount:</span>
              <span class="font-medium">{{ account.inviteCount() }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">pendingInviteCount:</span>
              <span class="font-medium" [class.text-yellow-600]="account.pendingInviteCount() > 0">
                {{ account.pendingInviteCount() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">invites:</span>
              <span class="font-mono text-[9px]">{{ account.invites().length > 0 ? '[' + account.invites().length + '
                invites]' : '[]' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">pendingInvites:</span>
              <span class="font-mono text-[9px]">{{ account.pendingInvites().length > 0 ? '[' +
                account.pendingInvites().length + ' pending]' : '[]' }}</span>
            </div>
          </div>
        </div>

        <!-- Profiles Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Profiles State</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">profilesLoading:</span>
              <span class="font-medium" [class.text-yellow-600]="account.profilesLoading()"
                [class.text-gray-600]="!account.profilesLoading()">
                {{ account.profilesLoading() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">profilesError:</span>
              <span class="font-mono text-[9px]" [class.text-red-600]="account.profilesError()">
                {{ account.profilesError() || 'null' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">hasProfiles:</span>
              <span class="font-medium" [class.text-success]="account.hasProfiles()"
                [class.text-gray-600]="!account.hasProfiles()">
                {{ account.hasProfiles() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">profileCount:</span>
              <span class="font-medium">{{ account.profileCount() }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">profiles:</span>
              <span class="font-mono text-[9px]">{{ account.profiles().size > 0 ? 'Map(' + account.profiles().size + ')'
                : 'Map(0)' }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-on-surface-variant font-mono text-[10px]">studentIds (active profile):</span>
              <div class="bg-surface rounded p-1">
                @if ((account.activeProfile()?.studentIds?.length ?? 0) > 0) {
                  <div class="font-mono text-[9px] break-all">
                    {{ account.activeProfile()!.studentIds }}
                  </div>
                } @else {
                  <div class="font-mono text-[9px]-variant">
                    []
                  </div>
                }
              </div>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-on-surface-variant font-mono text-[10px]">tokenPermissions (active team):</span>
              <div class="bg-surface rounded p-1">
                @if (profileState.tokenPermissions()) {
                  <pre class="font-mono text-[9px] break-all whitespace-pre-wrap">{{ profileState.tokenPermissions() | json }}</pre>
                } @else {
                  <div class="font-mono text-[9px]-variant italic">null</div>
                }
              </div>
            </div>
          </div>
        </div>

        <!-- Active Team/Profile Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Active Team/Profile State</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">hasActiveTeam:</span>
              <span class="font-medium" [class.text-success]="account.hasActiveTeam()"
                [class.text-gray-600]="!account.hasActiveTeam()">
                {{ account.hasActiveTeam() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">activeProfileId:</span>
              <span class="font-mono text-[9px]">{{ account.activeProfileId() || 'null' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">activeTeam:</span>
              <span class="font-mono text-[9px]">{{ account.activeTeam() ? account.activeTeam()?.name : 'null' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">activeProfile:</span>
              <span class="font-mono text-[9px]">{{ account.activeProfile() ? account.activeProfile()?.email : 'null'
                }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">activeRole:</span>
              <span class="font-medium" [class.text-purple-600]="account.activeRole() === 'admin'"
                [class.text-blue-600]="account.activeRole() === 'teacher'">
                {{ account.activeRole() || 'null' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">isActiveAdmin:</span>
              <span class="font-medium" [class.text-purple-600]="account.isActiveAdmin()"
                [class.text-gray-600]="!account.isActiveAdmin()">
                {{ account.isActiveAdmin() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">isActiveTeacher:</span>
              <span class="font-medium" [class.text-blue-600]="account.isActiveTeacher()"
                [class.text-gray-600]="!account.isActiveTeacher()">
                {{ account.isActiveTeacher() }}
              </span>
            </div>
          </div>
        </div>

        <!-- Verification Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Verification State</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">isEmailVerified:</span>
              <span class="font-medium" [class.text-success]="account.isEmailVerified()"
                [class.text-gray-600]="!account.isEmailVerified()">
                {{ account.isEmailVerified() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">emailVerifiedAt:</span>
              <span class="font-mono text-[9px]">
                {{ account.emailVerifiedAt() ? (account.emailVerifiedAt() | date:'short') : 'null' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">lastVerificationCodeSentAt:</span>
              <span class="font-mono text-[9px]">
                {{ account.lastVerificationCodeSentAt() ? (account.lastVerificationCodeSentAt() | date:'short') : 'null'
                }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">attemptsSinceLastCodeSent:</span>
              <span class="font-medium">{{ account.attemptsSinceLastCodeSent() || 'null' }}</span>
            </div>
          </div>
        </div>

        <!-- Raw Data Signal -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Raw Data Signal</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">data:</span>
              <span class="font-mono text-[9px]">{{ account.data() ? 'Account{...}' : 'null' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Active Team Context -->
      @if (account.activeTeam()) {
      <div class="space-y-2 p-2 bg-primary-container border border-primary rounded">
        <h4 class="text-xs font-medium text-on-primary-container">★ Active Team</h4>

        <div class="space-y-1 text-xs">
          <div class="flex justify-between">
            <span class="text-on-primary-container/70">Team:</span>
            <span class="font-semibold">{{ account.activeTeam()?.name }}</span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-primary-container/70">Type:</span>
            <span class="font-medium">{{ account.activeTeam()?.type }}</span>
          </div>

          @if (account.activeTeam()?.state) {
          <div class="flex justify-between">
            <span class="text-on-primary-container/70">State:</span>
            <span>{{ account.activeTeam()?.state }}</span>
          </div>
          }

          <div class="flex justify-between">
            <span class="text-on-primary-container/70">Your Role:</span>
            <span class="px-2 py-0.5 rounded-full text-[10px] font-medium"
              [class.bg-purple-100]="account.activeRole() === 'admin'"
              [class.text-purple-800]="account.activeRole() === 'admin'"
              [class.bg-blue-100]="account.activeRole() === 'teacher'"
              [class.text-blue-800]="account.activeRole() === 'teacher'">
              {{ account.activeRole() || 'No role' }}
            </span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-primary-container/70">Team ID:</span>
            <span class="font-mono text-[10px]">{{ account.activeTeam()?.id }}</span>
          </div>
        </div>

        <!-- AI Services -->
        <div class="mt-2 pt-2 border-t border-primary/30">
          <h5 class="text-[10px] font-semibold text-on-primary-container/80 mb-1">AI Services</h5>
          @if (account.activeTeamAiServices().length === 0) {
            <span class="text-[10px] text-on-primary-container/50 italic">No AI services configured</span>
          }
          @for (service of account.activeTeamAiServices(); track service.serviceName) {
            <div class="mt-1 space-y-0.5">
              <div class="flex items-center gap-1">
                <span class="inline-block w-1.5 h-1.5 rounded-full bg-primary"></span>
                <span class="text-[10px] font-semibold text-on-primary-container">{{ service.serviceName }}</span>
              </div>
              <div class="pl-3 space-y-0.5">
                <div class="flex justify-between">
                  <span class="text-[10px] text-on-primary-container/60">Partner daily limit:</span>
                  <span class="text-[10px] font-mono">{{ service.dailyLimit ?? '—' }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-[10px] text-on-primary-container/60">User daily limit:</span>
                  <span class="text-[10px] font-mono">{{ service.userDailyLimit ?? '—' }}</span>
                </div>
              </div>
            </div>
          }
        </div>
      </div>
      }

      <!-- User Information -->
      @if (account.hasData()) {
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">
          User Information
        </h4>

        <div class="space-y-1 text-xs">
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Full Name:</span>
            <span class="font-medium">{{ account.fullName() || "N/A" }}</span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">First Name:</span>
            <span class="font-medium">{{ account.firstName() || "N/A" }}</span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">Last Name:</span>
            <span class="font-medium">{{ account.lastName() || "N/A" }}</span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">Email:</span>
            <span class="font-medium">{{
              account.accountEmail() || "N/A"
              }}</span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">UID:</span>
            <span class="font-mono text-[10px]">{{
              account.uid() || "N/A"
              }}</span>
          </div>

          <div class="flex justify-between">
            <span class="text-on-surface-variant">Doc ID:</span>
            <span class="font-mono text-[10px]">{{
              account.id() || "N/A"
              }}</span>
          </div>
        </div>
      </div>

      <!-- Verification Status -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">
          Verification
        </h4>

        <div class="space-y-1 text-xs">
          <div class="flex items-center gap-2">
            @if (account.isEmailVerified()) {
            <span class="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-[10px]">✓ Verified</span>
            } @else {
            <span class="px-2 py-0.5 bg-gray-100 text-gray-800 rounded-full text-[10px]">Not Verified</span>
            }
          </div>

          @if (account.emailVerifiedAt()) {
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Verified At:</span>
            <span class="font-mono text-[10px]">{{
              account.emailVerifiedAt() | date : "short"
              }}</span>
          </div>
          } @if (account.lastVerificationCodeSentAt()) {
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Last Code Sent:</span>
            <span class="font-mono text-[10px]">{{
              account.lastVerificationCodeSentAt() | date : "short"
              }}</span>
          </div>
          } @if (account.attemptsSinceLastCodeSent()) {
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Attempts:</span>
            <span>{{ account.attemptsSinceLastCodeSent() }}</span>
          </div>
          }
        </div>
      </div>

      <!-- Special Badges -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">Badges</h4>

        <div class="flex flex-wrap gap-2">
          @if (account.isBlumeUser()) {
          <span class="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-medium">
            🏢 Blume Admin
          </span>
          } @if (account.hasPartners()) {
          <span class="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
            👥 Has Partners ({{ account.partnerIds().length }})
          </span>
          }
        </div>
      </div>

      <!-- Partners -->
      @if (account.hasPartners()) {
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">
          Partners ({{ account.partnerCount() }})
        </h4>

        <!-- Loading State -->
        @if (account.partnersLoading()) {
        <div class="flex items-center gap-2 text-xs-variant">
          <div class="w-2 h-2 rounded-full bg-yellow-500 animate-pulse"></div>
          <span>Loading partners...</span>
        </div>
        }

        <!-- Error State -->
        @if (account.partnersError()) {
        <div class="p-2 bg-error-container text-on-error-container rounded text-xs">
          <strong>Error:</strong> {{ account.partnersError() }}
        </div>
        }

        <!-- Partners List -->
        @if (account.hasPartnerData() && !account.partnersLoading()) {
        <div class="space-y-2 max-h-48 overflow-y-auto">
          @for (partner of account.partners(); track partner.id) {
          <div class="p-2 bg-surface rounded border border-outline-variant">
            <div class="text-xs font-medium">{{ partner.name || 'Unnamed' }}</div>
            <div class="text-[10px]-variant space-y-0.5 mt-1">
              <div>Type: {{ partner.type || 'N/A' }}</div>
              <div>State: {{ partner.state || 'N/A' }}</div>
              @if (partner.studentPopulation) {
              <div>Students: {{ partner.studentPopulation }}</div>
              }
              <div class="font-mono text-[9px]-variant/70">
                ID: {{ partner.id }}
              </div>
            </div>
          </div>
          }
        </div>
        } @else if (!account.partnersLoading()) {
        <div class="p-2 bg-surface-variant rounded text-center text-xs-variant">
          No partner data loaded
        </div>
        }
      </div>
      }

      <!-- Invites -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">
          Invites ({{ account.inviteCount() }})
        </h4>

        <!-- Loading State -->
        @if (account.invitesLoading()) {
        <div class="flex items-center gap-2 text-xs-variant">
          <div class="w-2 h-2 rounded-full bg-yellow-500 animate-pulse"></div>
          <span>Loading invites...</span>
        </div>
        }

        <!-- Error State -->
        @if (account.invitesError()) {
        <div class="p-2 bg-error-container text-on-error-container rounded text-xs">
          <strong>Error:</strong> {{ account.invitesError() }}
        </div>
        }

        <!-- Invites List -->
        @if (account.hasInvites() && !account.invitesLoading()) {
        <div class="space-y-2 max-h-48 overflow-y-auto">
          @for (invite of account.invites(); track invite.id) {
          <div class="p-2 bg-surface rounded border border-outline-variant">
            <div class="flex items-center justify-between">
              <div class="text-xs font-medium">{{ invite.partnerName || 'Unnamed Team' }}</div>
              <span class="px-2 py-0.5 rounded-full text-[10px]" [class.bg-yellow-100]="invite.status === 'pending'"
                [class.text-yellow-800]="invite.status === 'pending'"
                [class.bg-green-100]="invite.status === 'accepted'"
                [class.text-green-800]="invite.status === 'accepted'" [class.bg-red-100]="invite.status === 'declined'"
                [class.text-red-800]="invite.status === 'declined'">
                {{ invite.status }}
              </span>
            </div>
            <div class="text-[10px]-variant mt-1">
              From: {{ invite.invitedBy || 'Unknown' }}
            </div>
            @if (invite.date) {
            <div class="text-[9px]-variant/70 mt-0.5">
              {{ invite.date | date:'short' }}
            </div>
            }
          </div>
          }
        </div>

        <!-- Pending Count -->
        @if (account.pendingInviteCount() > 0) {
        <div class="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
          <strong>{{ account.pendingInviteCount() }}</strong> pending invite(s)
        </div>
        }
        } @else if (!account.invitesLoading()) {
        <div class="p-2 bg-surface-variant rounded text-center text-xs-variant">
          No invites
        </div>
        }
      </div>

      <!-- Profiles -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">
          Profiles ({{ account.profileCount() }})
          @if (account.activeProfileId()) {
          <span class="text-[10px]-variant">
            • Active: {{ account.activeProfileId() }}
          </span>
          }
        </h4>

        <!-- Loading State -->
        @if (account.profilesLoading()) {
        <div class="flex items-center gap-2 text-xs-variant">
          <div class="w-2 h-2 rounded-full bg-yellow-500 animate-pulse"></div>
          <span>Loading profiles...</span>
        </div>
        }

        <!-- Error State -->
        @if (account.profilesError()) {
        <div class="p-2 bg-error-container text-on-error-container rounded text-xs">
          <strong>Error:</strong> {{ account.profilesError() }}
        </div>
        }

        <!-- Token Permissions for Active Team -->
        <div class="p-2 rounded border"
          [class.border-primary]="profileState.tokenPermissions()"
          [class.bg-primary-container]="profileState.tokenPermissions()"
          [class.border-outline-variant]="!profileState.tokenPermissions()"
          [class.bg-surface-variant]="!profileState.tokenPermissions()">
          <div class="flex items-center justify-between mb-1">
            <span class="text-[10px] font-semibold-variant">tokenPermissions</span>
            <span class="text-[9px]-variant">blumeProfiles claim · active team</span>
          </div>
          @if (profileState.tokenPermissions(); as tp) {
            <pre class="font-mono text-[9px] break-all whitespace-pre-wrap">{{ tp | json }}</pre>
          } @else {
            <div class="text-[9px]-variant italic">No blumeProfiles claim for active team</div>
          }
        </div>

        <!-- App-access computed signals -->
        <div class="space-y-1.5">
          <!-- adminPortalTeams -->
          <div class="p-2 rounded border border-outline-variant bg-surface-variant">
            <div class="flex items-center justify-between mb-1">
              <span class="text-[10px] font-semibold-variant">adminPortalTeams</span>
              <span class="text-[9px] font-mono px-1.5 py-0.5 rounded-full"
                [class.bg-primary]="account.adminPortalTeams().length > 0"
                [class.text-on-primary]="account.adminPortalTeams().length > 0"
                [class.bg-surface]="account.adminPortalTeams().length === 0"
                [class.text-on-surface-variant]="account.adminPortalTeams().length === 0">
                {{ account.adminPortalTeams().length }}
              </span>
            </div>
            @if (account.adminPortalTeams().length > 0) {
              <div class="flex flex-col gap-1">
                @for (profile of account.adminPortalTeams(); track profile.email) {
                  <div class="bg-surface rounded p-1 space-y-0.5">
                    <div class="font-semibold text-[9px]">{{ profile.firstName }} {{ profile.lastName }}</div>
                    <div class="font-mono text-[8px] text-on-surface-variant break-all">{{ profile.email }}</div>
                    <div class="font-mono text-[8px]">role: {{ profile.role || '—' }}</div>
                  </div>
                }
              </div>
            } @else {
              <div class="text-[9px]-variant italic">No admin-portal permissions</div>
            }
          </div>

          <!-- notebookTeams -->
          <div class="p-2 rounded border border-outline-variant bg-surface-variant">
            <div class="flex items-center justify-between mb-1">
              <span class="text-[10px] font-semibold-variant">notebookTeams</span>
              <span class="text-[9px] font-mono px-1.5 py-0.5 rounded-full"
                [class.bg-primary]="account.notebookTeams().length > 0"
                [class.text-on-primary]="account.notebookTeams().length > 0"
                [class.bg-surface]="account.notebookTeams().length === 0"
                [class.text-on-surface-variant]="account.notebookTeams().length === 0">
                {{ account.notebookTeams().length }}
              </span>
            </div>
            @if (account.notebookTeams().length > 0) {
              <div class="flex flex-col gap-1">
                @for (profile of account.notebookTeams(); track profile.email) {
                  <div class="bg-surface rounded p-1 space-y-0.5">
                    <div class="font-semibold text-[9px]">{{ profile.firstName }} {{ profile.lastName }}</div>
                    <div class="font-mono text-[8px] text-on-surface-variant break-all">{{ profile.email }}</div>
                    <div class="font-mono text-[8px]">role: {{ profile.role || '—' }}</div>
                  </div>
                }
              </div>
            } @else {
              <div class="text-[9px]-variant italic">No notebook permissions</div>
            }
          </div>

          <!-- academyTeams -->
          <div class="p-2 rounded border border-outline-variant bg-surface-variant">
            <div class="flex items-center justify-between mb-1">
              <span class="text-[10px] font-semibold-variant">academyTeams</span>
              <span class="text-[9px] font-mono px-1.5 py-0.5 rounded-full"
                [class.bg-primary]="account.academyTeams().length > 0"
                [class.text-on-primary]="account.academyTeams().length > 0"
                [class.bg-surface]="account.academyTeams().length === 0"
                [class.text-on-surface-variant]="account.academyTeams().length === 0">
                {{ account.academyTeams().length }}
              </span>
            </div>
            @if (account.academyTeams().length > 0) {
              <div class="flex flex-col gap-1">
                @for (profile of account.academyTeams(); track profile.email) {
                  <div class="bg-surface rounded p-1 space-y-0.5">
                    <div class="font-semibold text-[9px]">{{ profile.firstName }} {{ profile.lastName }}</div>
                    <div class="font-mono text-[8px] text-on-surface-variant break-all">{{ profile.email }}</div>
                    <div class="font-mono text-[8px]">role: {{ profile.role || '—' }}</div>
                  </div>
                }
              </div>
            } @else {
              <div class="text-[9px]-variant italic">No academy permissions</div>
            }
          </div>
        </div>

        <!-- Profiles List -->
        @if (account.hasProfiles() && !account.profilesLoading()) {
        <div class="space-y-2 max-h-64 overflow-y-auto">
          @for (entry of account.profiles() | keyvalue; track entry.key) {
          <div class="p-3 bg-surface rounded border border-outline-variant space-y-2"
            [class.border-primary]="entry.key === account.activeProfileId()"
            [class.bg-primary-container]="entry.key === account.activeProfileId()">

            <!-- Header with Name and Role -->
            <div class="flex items-center justify-between">
              <div class="text-xs font-semibold">
                {{ entry.value.firstName }} {{ entry.value.lastName }}
              </div>
              @if (entry.value.role) {
              <span class="px-2 py-0.5 rounded-full text-[10px] font-medium"
                [class.bg-purple-100]="entry.value.role === 'admin'"
                [class.text-purple-800]="entry.value.role === 'admin'"
                [class.bg-blue-100]="entry.value.role === 'teacher'"
                [class.text-blue-800]="entry.value.role === 'teacher'"
                [class.bg-gray-100]="entry.value.role !== 'admin' && entry.value.role !== 'teacher'"
                [class.text-gray-800]="entry.value.role !== 'admin' && entry.value.role !== 'teacher'">
                {{ entry.value.role }}
              </span>
              }
            </div>

            <!-- Profile Details -->
            <div class="text-[10px]-variant space-y-1">
              <div class="flex justify-between">
                <span class="opacity-70">Team ID:</span>
                <span class="font-mono">{{ entry.key }}</span>
              </div>

              <div class="flex justify-between">
                <span class="opacity-70">Email:</span>
                <span class="font-mono">{{ entry.value.email }}</span>
              </div>

              @if (entry.value.uid) {
              <div class="flex justify-between">
                <span class="opacity-70">UID:</span>
                <span class="font-mono">{{ entry.value.uid }}</span>
              </div>
              }

              @if (entry.value.jobTitle) {
              <div class="flex justify-between">
                <span class="opacity-70">Job Title:</span>
                <span>{{ entry.value.jobTitle }}</span>
              </div>
              }

              @if (entry.value.permissions && entry.value.permissions.length > 0) {
              <div>
                <span class="opacity-70">Permissions ({{ entry.value.permissions.length }}):</span>
                <div class="flex flex-wrap gap-1 mt-1">
                  @for (perm of entry.value.permissions; track perm) {
                  <span class="font-mono text-[8px] bg-surface-variant px-1.5 py-0.5 rounded">{{ perm }}</span>
                  }
                </div>
              </div>
              }

              @if (entry.value.inviteAccepted !== undefined) {
              <div class="flex items-center gap-1">
                <span class="opacity-70">Invite:</span>
                <span [class.text-success]="entry.value.inviteAccepted"
                  [class.text-gray-600]="!entry.value.inviteAccepted">
                  {{ entry.value.inviteAccepted ? 'Accepted' : 'Pending' }}
                </span>
              </div>
              }

              @if (entry.value.createdAt) {
              <div class="flex justify-between">
                <span class="opacity-70">Created:</span>
                <span class="font-mono text-[9px]">{{ entry.value.createdAt | date:'short' }}</span>
              </div>
              }
            </div>

            <!-- Active Badge -->
            @if (entry.key === account.activeProfileId()) {
            <div class="flex items-center gap-1 pt-1 border-t border-outline-variant">
              <span class="px-2 py-0.5 bg-primary text-on-primary rounded-full text-[9px] font-medium">
                ★ Active Profile
              </span>
            </div>
            }
          </div>
          }
        </div>
        } @else if (!account.profilesLoading()) {
        <div class="p-2 bg-surface-variant rounded text-center text-xs-variant">
          No profiles loaded
        </div>
        }
      </div>

      <!-- Auth Token -->
      <div class="space-y-2">
        <div class="flex items-center justify-between">
          <h4 class="text-xs font-medium-variant">🔑 Auth</h4>
          <button class="text-[9px] px-1.5 py-0.5 rounded bg-surface-variant hover:bg-outline-variant transition-colors"
            (click)="refreshAuthToken()">
            Refresh
          </button>
        </div>
        @if (authToken()) {
        <pre class="p-2 bg-surface-variant rounded text-[9px] font-mono overflow-x-auto whitespace-pre-wrap break-all">{{ authToken() | json }}</pre>
        } @else {
        <div class="p-2 bg-surface-variant rounded text-center text-xs-variant">Not authenticated</div>
        }
      </div>

      <!-- Raw Data (Collapsible) -->
      <details class="space-y-2">
        <summary class="text-xs font-medium-variant cursor-pointer">
          Raw Data
        </summary>
        <pre class="p-2 bg-surface rounded text-[10px] overflow-x-auto">{{
          account.data() | json
        }}</pre>
      </details>

      <!-- Metadata -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">Metadata</h4>

        <div class="space-y-1 text-xs">
          @if (account.lastUpdated()) {
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Last Updated:</span>
            <span class="font-mono text-[10px]">{{
              account.lastUpdated() | date : "short"
              }}</span>
          </div>
          } @if (account.data()?.createdAt) {
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Created At:</span>
            <span class="font-mono text-[10px]">{{
              account.data()?.createdAt | date : "short"
              }}</span>
          </div>
          } @if (account.data()?.updatedAt) {
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Updated At:</span>
            <span class="font-mono text-[10px]">{{
              account.data()?.updatedAt | date : "short"
              }}</span>
          </div>
          }
        </div>
      </div>
      } @else {
      <div class="p-3 bg-surface-variant rounded text-center text-xs-variant">
        No account data loaded
      </div>
      }
    </div>
    }
  </div>

  <!-- ========== CALENDAR STATE ========== -->
  <div class="space-y-3 bg-surface rounded-lg border border-outline-variant select-none overflow-hidden"
    [class.pb-4]="calendarExpanded()">
    <div
      class="flex items-center bg-surface clickable justify-between text-lg font-semibold px-3 py-4 cursor-pointer"
      [class.border-b]="calendarExpanded()" (click)="toggleCalendarExpanded()">
      <span>📅 Calendar State (Component)</span>
      <button class="p-2 hover:bg-surface-variant rounded transition-colors">
        @if (calendarExpanded()) {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
        } @else {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
        }
      </button>
    </div>

    <!-- Collapsible Content -->
    @if (calendarExpanded()) {
    <div class="space-y-3 pb-4 px-3">
      <!-- Status Indicators -->
      <div class="p-2 bg-surface-variant rounded">
        <div class="space-y-1 text-xs">
          <div class="flex items-center gap-2">
            <div class="w-2 h-2 rounded-full" [class.bg-success]="calendar.initialized()"
              [class.bg-gray-400]="!calendar.initialized()"></div>
            <span>Initialized: {{ calendar.initialized() }}</span>
          </div>
          <div class="flex items-center gap-2">
            <div class="w-2 h-2 rounded-full" [class.bg-success]="calendar.isReady()"
              [class.bg-gray-400]="!calendar.isReady()"></div>
            <span>Ready: {{ calendar.isReady() }}</span>
          </div>
        </div>
      </div>

      <!-- State Section - All Public Signals -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">📊 State (All Public Signals)</h4>

        <!-- Selection State Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Selection State</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">selectedYear:</span>
              <span class="font-medium">{{ calendar.selectedYear() }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">selectedMonth:</span>
              <span class="font-mono text-[9px]">{{ calendar.selectedMonth() ?? 'null' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">selectedWeek:</span>
              <span class="font-mono text-[9px]">{{ calendar.selectedWeek() ?? 'null' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">selectedDay:</span>
              <span class="font-mono text-[9px]">{{ calendar.selectedDay() ? (calendar.selectedDay() | date:'short') :
                'null' }}</span>
            </div>
          </div>
        </div>

        <!-- Boolean State Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Boolean State</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">hasMonthSelected:</span>
              <span class="font-medium" [class.text-success]="calendar.hasMonthSelected()"
                [class.text-gray-600]="!calendar.hasMonthSelected()">
                {{ calendar.hasMonthSelected() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">hasWeekSelected:</span>
              <span class="font-medium" [class.text-success]="calendar.hasWeekSelected()"
                [class.text-gray-600]="!calendar.hasWeekSelected()">
                {{ calendar.hasWeekSelected() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">hasDaySelected:</span>
              <span class="font-medium" [class.text-success]="calendar.hasDaySelected()"
                [class.text-gray-600]="!calendar.hasDaySelected()">
                {{ calendar.hasDaySelected() }}
              </span>
            </div>
          </div>
        </div>

        <!-- Display Helpers -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Display Helpers</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">selectedMonthName:</span>
              <span class="font-medium">{{ calendar.selectedMonthName() ?? 'null' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">selectedMonthNameShort:</span>
              <span class="font-medium">{{ calendar.selectedMonthNameShort() ?? 'null' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">selectedYearMonthKey:</span>
              <span class="font-mono text-[9px]">{{ calendar.selectedYearMonthKey() ?? 'null' }}</span>
            </div>
          </div>
        </div>

        <!-- Display Names (Formatted for UI) -->
        <div class="p-2 bg-primary-container border border-primary rounded">
          <h5 class="text-[10px] font-semibold text-on-primary-container mb-2">📝 Display Names (For UI)</h5>
          <div class="space-y-1 text-xs">
            <div class="flex flex-col gap-1">
              <span class="text-on-primary-container/70 font-mono text-[10px]">monthDisplayName:</span>
              <span class="font-semibold text-on-primary-container">{{ calendar.monthDisplayName() ?? 'null' }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-on-primary-container/70 font-mono text-[10px]">weekDisplayName:</span>
              <span class="font-semibold text-on-primary-container">{{ calendar.weekDisplayName() ?? 'null' }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-on-primary-container/70 font-mono text-[10px]">dayOfMonthDisplayName:</span>
              <span class="font-semibold text-lg text-on-primary-container">{{ calendar.dayOfMonthDisplayName() ??
                'null' }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-on-primary-container/70 font-mono text-[10px]">dayDisplayName:</span>
              <span class="font-semibold text-on-primary-container">{{ calendar.dayDisplayName() ?? 'null' }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-on-primary-container/70 font-mono text-[10px]">dayDisplayNameLong:</span>
              <span class="font-semibold text-on-primary-container">{{ calendar.dayDisplayNameLong() ?? 'null' }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-on-primary-container/70 font-mono text-[10px]">dayDisplayNameShort:</span>
              <span class="font-semibold text-on-primary-container">{{ calendar.dayDisplayNameShort() ?? 'null'
                }}</span>
            </div>
          </div>
        </div>

        <!-- Current Date Comparisons -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Current Date Comparisons</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">isCurrentYear:</span>
              <span class="font-medium" [class.text-success]="calendar.isCurrentYear()"
                [class.text-gray-600]="!calendar.isCurrentYear()">
                {{ calendar.isCurrentYear() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">isCurrentMonth:</span>
              <span class="font-medium" [class.text-success]="calendar.isCurrentMonth()"
                [class.text-gray-600]="!calendar.isCurrentMonth()">
                {{ calendar.isCurrentMonth() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">isToday:</span>
              <span class="font-medium" [class.text-success]="calendar.isToday()"
                [class.text-gray-600]="!calendar.isToday()">
                {{ calendar.isToday() }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Current Selection Summary -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">Current Selection</h4>

        <div class="p-3 bg-primary-container border border-primary rounded">
          <div class="space-y-2 text-xs">
            <div class="flex items-center gap-2">
              <span class="text-on-primary-container/70">Year:</span>
              <span class="font-bold text-lg">{{ calendar.selectedYear() }}</span>
              @if (calendar.isCurrentYear()) {
              <span class="px-2 py-0.5 bg-primary text-on-primary rounded-full text-[9px]">Current</span>
              }
            </div>

            @if (calendar.hasMonthSelected()) {
            <div class="flex items-center gap-2">
              <span class="text-on-primary-container/70">Month:</span>
              <span class="font-semibold">{{ calendar.selectedMonthName() }}</span>
              @if (calendar.isCurrentMonth()) {
              <span class="px-2 py-0.5 bg-primary text-on-primary rounded-full text-[9px]">Current</span>
              }
            </div>
            } @else {
            <div class="text-on-primary-container/50 italic">No month selected</div>
            }

            @if (calendar.hasWeekSelected()) {
            <div class="flex items-center gap-2">
              <span class="text-on-primary-container/70">ISO Week:</span>
              <span class="font-semibold">{{ calendar.selectedWeek() }}</span>
            </div>
            } @else {
            <div class="text-on-primary-container/50 italic">No week selected</div>
            }

            @if (calendar.hasDaySelected()) {
            <div class="flex items-center gap-2">
              <span class="text-on-primary-container/70">Day:</span>
              <span class="font-semibold">{{ calendar.selectedDay() | date:'fullDate' }}</span>
              @if (calendar.isToday()) {
              <span class="px-2 py-0.5 bg-primary text-on-primary rounded-full text-[9px]">Today</span>
              }
            </div>
            } @else {
            <div class="text-on-primary-container/50 italic">No day selected</div>
            }
          </div>
        </div>
      </div>

      <!-- Raw Data (Collapsible) -->
      <details class="space-y-2">
        <summary class="text-xs font-medium-variant cursor-pointer">
          Raw Data
        </summary>
        <pre class="p-2 bg-surface rounded text-[10px] overflow-x-auto">{{
          calendar.data() | json
        }}</pre>
      </details>
    </div>
    }
  </div>

  <!-- ========== MY STUDENTS STATE ========== -->
  <div class="space-y-3 bg-surface rounded-lg border border-outline-variant select-none overflow-hidden"
    [class.pb-4]="myStudentsExpanded()">
    <div
      class="flex items-center bg-surface clickable justify-between text-lg font-semibold px-3 py-4 cursor-pointer"
      [class.border-b]="myStudentsExpanded()" (click)="toggleMyStudentsExpanded()">
      <div class="flex items-center gap-2">
        <span>👥 My Students State</span>
      </div>
      <button class="p-2 hover:bg-surface-variant rounded transition-colors">
        @if (myStudentsExpanded()) {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
        } @else {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
        }
      </button>
    </div>

    <!-- Collapsible Content -->
    @if (myStudentsExpanded()) {
    <div class="space-y-3 pb-4 px-3">
      <!-- Status Indicators -->
      <app-state-status-indicators [initialized]="myStudents.initialized()" [loading]="myStudents.loading()"
        [isReady]="myStudents.isReady()" [hasData]="myStudents.hasData()" [error]="myStudents.error()" />

      <!-- Data Summary Card -->
      @if (myStudents.initialized()) {
      <div class="p-3 rounded border bg-surface-variant border-outline-variant">
        <div class="text-xs space-y-2">
          <div class="flex items-center gap-2">
            <span class="text-on-surface-variant font-semibold">📊 Loaded from Firestore</span>
          </div>
          <div class="grid grid-cols-2 gap-2 text-[10px]">
            <div>
              <span class="text-on-surface-variant">Students:</span>
              <span class="font-semibold ml-1">{{ myStudents.count() }}</span>
            </div>
            <div>
              <span class="text-on-surface-variant">Student IDs:</span>
              <span class="font-semibold ml-1">{{ myStudents.studentIds().length }}</span>
            </div>
          </div>
        </div>
      </div>
      }

      <!-- State Section - All Public Signals -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">📊 State (All Public Signals)</h4>

        <!-- Basic State Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Basic State</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">initialized:</span>
              <span class="font-medium" [class.text-success]="myStudents.initialized()"
                [class.text-gray-600]="!myStudents.initialized()">
                {{ myStudents.initialized() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">loading:</span>
              <span class="font-medium" [class.text-yellow-600]="myStudents.loading()"
                [class.text-gray-600]="!myStudents.loading()">
                {{ myStudents.loading() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">error:</span>
              <span class="font-mono text-[9px]" [class.text-red-600]="myStudents.error()">
                {{ myStudents.error() || 'null' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">lastUpdated:</span>
              <span class="font-mono text-[9px]">
                {{ myStudents.lastUpdated() ? (myStudents.lastUpdated() | date:'short') : 'null' }}
              </span>
            </div>
          </div>
        </div>

        <!-- Context Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Context</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">activeTeamId:</span>
              <span class="font-mono text-[9px]">{{ myStudents.activeTeamId() || 'null' }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-on-surface-variant font-mono text-[10px]">studentIds:</span>
              <div class="bg-surface rounded p-1">
                @if (myStudents.studentIds().length > 0) {
                  <div class="font-mono text-[9px] break-all">
                    {{ myStudents.studentIds() }}
                  </div>
                } @else {
                  <div class="font-mono text-[9px]-variant">
                    []
                  </div>
                }
              </div>
            </div>
          </div>
        </div>

        <!-- Computed State Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Computed State</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">isReady:</span>
              <span class="font-medium" [class.text-success]="myStudents.isReady()"
                [class.text-gray-600]="!myStudents.isReady()">
                {{ myStudents.isReady() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">hasData:</span>
              <span class="font-medium" [class.text-success]="myStudents.hasData()"
                [class.text-gray-600]="!myStudents.hasData()">
                {{ myStudents.hasData() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">count:</span>
              <span class="font-medium">{{ myStudents.count() }}</span>
            </div>
          </div>
        </div>

        <!-- Data Signal -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Data Signal</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">data:</span>
              <span class="font-mono text-[9px]">{{ myStudents.data().length > 0 ? '[' + myStudents.data().length + '
                students]' : '[]' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Students List (if any) -->
      @if (myStudents.hasData()) {
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">
          Students ({{ myStudents.count() }})
        </h4>

        <div class="space-y-2 max-h-64 overflow-y-auto">
          @for (student of myStudents.studentsSorted(); track student.id || student.studentId) {
          <div class="p-2 bg-surface rounded border border-outline-variant">
            <div class="text-xs font-medium">
              {{ student.firstName }} {{ student.lastName }}
            </div>
            <div class="text-[10px]-variant space-y-0.5 mt-1">
              @if (student.grade) {
              <div>Grade: {{ student.grade }}</div>
              }
              @if (student.school) {
              <div>School: {{ student.school }}</div>
              }
              @if (student.gender) {
              <div>Gender: {{ student.gender }}</div>
              }
              @if (student.englishLearner) {
              <div class="text-blue-600 font-medium">✓ English Learner</div>
              }
              <div class="font-mono text-[9px]-variant/70 mt-1">
                ID: {{ student.id || student.studentId }}
              </div>
            </div>
          </div>
          }
        </div>

        <!-- English Learners Summary -->
        @if (myStudents.englishLearners().length > 0) {
        <div class="p-2 bg-blue-50 border border-blue-200 rounded text-xs">
          <strong>{{ myStudents.englishLearners().length }}</strong> English Learner(s)
        </div>
        }
      </div>
      } @else if (myStudents.initialized() && !myStudents.loading()) {
      <div class="p-3 bg-surface-variant rounded text-center text-xs-variant">
        No students assigned
      </div>
      }

      <!-- Raw Data (Collapsible) -->
      <details class="space-y-2">
        <summary class="text-xs font-medium-variant cursor-pointer">
          Raw Data
        </summary>
        <pre class="p-2 bg-surface rounded text-[10px] overflow-x-auto">{{
          myStudents.data() | json
        }}</pre>
      </details>
    </div>
    }
  </div>

  <!-- ========== MY MEASUREMENTS STATE ========== -->
  <div class="space-y-3 bg-surface rounded-lg border border-outline-variant select-none overflow-hidden"
    [class.pb-4]="myMeasurementsExpanded()">
    <div
      class="flex items-center bg-surface clickable justify-between text-lg font-semibold px-3 py-4 cursor-pointer"
      [class.border-b]="myMeasurementsExpanded()" (click)="toggleMyMeasurementsExpanded()">
      <div class="flex items-center gap-2">
        <span>📏 My Measurements State</span>
      </div>
      <button class="p-2 hover:bg-surface-variant rounded transition-colors">
        @if (myMeasurementsExpanded()) {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
        } @else {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
        }
      </button>
    </div>

    <!-- Collapsible Content -->
    @if (myMeasurementsExpanded()) {
    <div class="space-y-3 pb-4 px-3">
      <!-- Status Indicators -->
      <app-state-status-indicators [initialized]="myMeasurements.initialized()" [loading]="myMeasurements.loading()"
        [isReady]="myMeasurements.isReady()" [hasData]="myMeasurements.hasData()" [error]="myMeasurements.error()" />

      <!-- Data Summary Card -->
      @if (myMeasurements.initialized()) {
      <div class="p-3 rounded border bg-surface-variant border-outline-variant">
        <div class="text-xs space-y-2">
          <div class="flex items-center gap-2">
            <span class="text-on-surface-variant font-semibold">
              📊 {{ myMeasurements.hydratedFromCache() ? 'Hydrated from cache' : 'Loaded from Firestore' }}
            </span>
          </div>
          <div class="grid grid-cols-2 gap-2 text-[10px]">
            <div>
              <span class="text-on-surface-variant">Measurements:</span>
              <span class="font-semibold ml-1">{{ myMeasurements.count() }}</span>
            </div>
            <div>
              <span class="text-on-surface-variant">Student IDs:</span>
              <span class="font-semibold ml-1">{{ myMeasurements.studentIds().length }}</span>
            </div>
          </div>
        </div>
      </div>
      }

      <!-- State Section - All Public Signals -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">📊 State (All Public Signals)</h4>

        <!-- Basic State Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Basic State</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">initialized:</span>
              <span class="font-medium" [class.text-success]="myMeasurements.initialized()"
                [class.text-gray-600]="!myMeasurements.initialized()">
                {{ myMeasurements.initialized() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">loading:</span>
              <span class="font-medium" [class.text-yellow-600]="myMeasurements.loading()"
                [class.text-gray-600]="!myMeasurements.loading()">
                {{ myMeasurements.loading() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">error:</span>
              <span class="font-mono text-[9px]" [class.text-red-600]="myMeasurements.error()">
                {{ myMeasurements.error() || 'null' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">lastUpdated:</span>
              <span class="font-mono text-[9px]">
                {{ myMeasurements.lastUpdated() ? (myMeasurements.lastUpdated() | date:'short') : 'null' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">lastSync:</span>
              <span class="font-mono text-[9px]">
                {{ myMeasurements.lastSync() ? (myMeasurements.lastSync() | date:'short') : 'null' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">hydratedFromCache:</span>
              <span class="font-medium" [class.text-blue-600]="myMeasurements.hydratedFromCache()">
                {{ myMeasurements.hydratedFromCache() }}
              </span>
            </div>
          </div>
        </div>

        <!-- Context Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Context</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">activeTeamId:</span>
              <span class="font-mono text-[9px]">{{ myMeasurements.activeTeamId() || 'null' }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-on-surface-variant font-mono text-[10px]">studentIds:</span>
              <div class="bg-surface rounded p-1">
                @if (myMeasurements.studentIds().length > 0) {
                  <div class="font-mono text-[9px] break-all">
                    {{ myMeasurements.studentIds() }}
                  </div>
                } @else {
                  <div class="font-mono text-[9px]-variant">[]</div>
                }
              </div>
            </div>
          </div>
        </div>

        <!-- Computed State Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Computed State</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">isReady:</span>
              <span class="font-medium" [class.text-success]="myMeasurements.isReady()"
                [class.text-gray-600]="!myMeasurements.isReady()">
                {{ myMeasurements.isReady() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">hasData:</span>
              <span class="font-medium" [class.text-success]="myMeasurements.hasData()"
                [class.text-gray-600]="!myMeasurements.hasData()">
                {{ myMeasurements.hasData() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">count:</span>
              <span class="font-medium">{{ myMeasurements.count() }}</span>
            </div>
          </div>
        </div>

        <!-- Data Signal -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Data Signal</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">data:</span>
              <span class="font-mono text-[9px]">
                {{ myMeasurements.data().length > 0 ? '[' + myMeasurements.data().length + ' measurements]' : '[]' }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      @if (myMeasurements.initialized() && !myMeasurements.loading() && !myMeasurements.hasData()) {
      <div class="p-3 bg-surface-variant rounded text-center text-xs-variant">
        No measurements found
      </div>
      }

      <!-- Raw Data (Collapsible) -->
      <details class="space-y-2">
        <summary class="text-xs font-medium-variant cursor-pointer">
          Raw Data
        </summary>
        <pre class="p-2 bg-surface rounded text-[10px] overflow-x-auto">{{ myMeasurements.data() | json }}</pre>
      </details>
    </div>
    }
  </div>

  <!-- ========== MY GOALS STATE ========== -->
  <div class="space-y-3 bg-surface rounded-lg border border-outline-variant select-none overflow-hidden"
    [class.pb-4]="myGoalsExpanded()">
    <div
      class="flex items-center bg-surface clickable justify-between text-lg font-semibold px-3 py-4 cursor-pointer"
      [class.border-b]="myGoalsExpanded()" (click)="toggleMyGoalsExpanded()">
      <span>🎯 My Goals State (Team-Scoped)</span>
      <button class="p-2 hover:bg-surface-variant rounded transition-colors">
        @if (myGoalsExpanded()) {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
        } @else {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
        }
      </button>
    </div>

    <!-- Collapsible Content -->
    @if (myGoalsExpanded()) {
    <div class="space-y-3 pb-4 px-3">
      <!-- Status Indicators -->
      <app-state-status-indicators [initialized]="myGoals.initialized()" [loading]="myGoals.loading()"
        [isReady]="myGoals.isReady()" [hasData]="myGoals.hasData()" [error]="myGoals.error()" />

      <!-- State Section - All Public Signals -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">📊 State (All Public Signals)</h4>

        <!-- Basic State Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Basic State</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">initialized:</span>
              <span class="font-medium" [class.text-success]="myGoals.initialized()"
                [class.text-gray-600]="!myGoals.initialized()">
                {{ myGoals.initialized() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">loading:</span>
              <span class="font-medium" [class.text-yellow-600]="myGoals.loading()"
                [class.text-gray-600]="!myGoals.loading()">
                {{ myGoals.loading() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">error:</span>
              <span class="font-mono text-[9px]" [class.text-red-600]="myGoals.error()">
                {{ myGoals.error() || 'null' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">lastUpdated:</span>
              <span class="font-mono text-[9px]">
                {{ myGoals.lastUpdated() ? (myGoals.lastUpdated() | date:'short') : 'null' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">lastSync:</span>
              <span class="font-mono text-[9px]">
                {{ myGoals.lastSync() ? (myGoals.lastSync() | date:'short') : 'null' }}
              </span>
            </div>
          </div>
        </div>

        <!-- Context Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Context</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">activeTeamId:</span>
              <span class="font-mono text-[9px]">{{ myGoals.activeTeamId() || 'null' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">caseload:</span>
              <span class="font-mono text-[9px]">{{ myGoals.caseload().length > 0 ? '[' + myGoals.caseload().length + '
                ids]' : '[]' }}</span>
            </div>
          </div>
        </div>

        <!-- Computed State Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Computed State</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">isReady:</span>
              <span class="font-medium" [class.text-success]="myGoals.isReady()"
                [class.text-gray-600]="!myGoals.isReady()">
                {{ myGoals.isReady() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">hasData:</span>
              <span class="font-medium" [class.text-success]="myGoals.hasData()"
                [class.text-gray-600]="!myGoals.hasData()">
                {{ myGoals.hasData() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">count:</span>
              <span class="font-medium">{{ myGoals.count() }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">activeGoalsCount:</span>
              <span class="font-medium text-success">{{ myGoals.activeGoalsCount() }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">archivedGoalsCount:</span>
              <span class="font-medium text-gray-600">{{ myGoals.archivedGoalsCount() }}</span>
            </div>
          </div>
        </div>

        <!-- Data Signal -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Data Signal</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">data:</span>
              <span class="font-mono text-[9px]">{{ myGoals.data().length > 0 ? '[' + myGoals.data().length + ' goals]'
                : '[]' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Goals List (if any) -->
      @if (myGoals.hasData()) {
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">
          Goals ({{ myGoals.count() }})
        </h4>

        <!-- Active vs Archived Summary -->
        <div class="flex gap-2">
          <div class="flex-1 p-2 bg-green-50 border border-green-200 rounded text-center">
            <div class="text-lg font-bold text-green-700">{{ myGoals.activeGoalsCount() }}</div>
            <div class="text-[10px] text-success">Active</div>
          </div>
          <div class="flex-1 p-2 bg-gray-50 border border-gray-200 rounded text-center">
            <div class="text-lg font-bold text-gray-700">{{ myGoals.archivedGoalsCount() }}</div>
            <div class="text-[10px] text-gray-600">Archived</div>
          </div>
        </div>

        <div class="space-y-2 max-h-64 overflow-y-auto">
          @for (goal of myGoals.goalsSorted(); track goal.id) {
          <div class="p-2 bg-surface rounded border border-outline-variant" [class.opacity-50]="goal.archived">
            <div class="flex items-center justify-between mb-1">
              <div class="text-xs font-medium truncate max-w-[70%]">
                {{ goal.skillAreaIds.join(', ') || '—' }}
              </div>
              @if (goal.archived) {
              <span class="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-[9px]">Archived</span>
              }
            </div>
            <div class="text-[10px]-variant space-y-0.5">
              <div>Student: {{ goal.studentId }}</div>
              @if (goal.skillIds.length) {
              <div>Skills: {{ goal.skillIds.length }}</div>
              }
              @if (goal.milestones.length) {
              <div>Milestones: {{ goal.milestones.length }}</div>
              }
              @if (goal.endDate) {
              <div>End: {{ goal.endDate }}</div>
              }
              <div class="font-mono text-[9px]-variant/70 mt-1">
                ID: {{ goal.id }}
              </div>
            </div>
          </div>
          }
        </div>
      </div>
      } @else if (myGoals.initialized() && !myGoals.loading()) {
      <div class="p-3 bg-surface-variant rounded text-center text-xs-variant">
        No goals for students in caseload
      </div>
      }

      <!-- Raw Data (Collapsible) -->
      <details class="space-y-2">
        <summary class="text-xs font-medium-variant cursor-pointer">
          Raw Data
        </summary>
        <pre class="p-2 bg-surface rounded text-[10px] overflow-x-auto">{{
          myGoals.data() | json
        }}</pre>
      </details>
    </div>
    }
  </div>

  <!-- ========== MY OBJECTIVES STATE ========== -->
  <div class="space-y-3 bg-surface rounded-lg border border-outline-variant select-none overflow-hidden"
    [class.pb-4]="myObjectivesExpanded()">
    <div
      class="flex items-center bg-surface clickable justify-between text-lg font-semibold px-3 py-4 cursor-pointer"
      [class.border-b]="myObjectivesExpanded()" (click)="toggleMyObjectivesExpanded()">
      <span>📝 My Objectives State (Team-Scoped)</span>
      <button class="p-2 hover:bg-surface-variant rounded transition-colors">
        @if (myObjectivesExpanded()) {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
        } @else {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
        }
      </button>
    </div>

    <!-- Collapsible Content -->
    @if (myObjectivesExpanded()) {
    <div class="space-y-3 pb-4 px-3">
      <!-- Status Indicators -->
      <app-state-status-indicators [initialized]="myObjectives.initialized()" [loading]="myObjectives.loading()"
        [isReady]="myObjectives.isReady()" [hasData]="myObjectives.hasData()" [error]="myObjectives.error()" />

      <!-- State Section - All Public Signals -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">📊 State (All Public Signals)</h4>

        <!-- Basic State Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Basic State</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">initialized:</span>
              <span class="font-medium" [class.text-success]="myObjectives.initialized()"
                [class.text-gray-600]="!myObjectives.initialized()">
                {{ myObjectives.initialized() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">loading:</span>
              <span class="font-medium" [class.text-yellow-600]="myObjectives.loading()"
                [class.text-gray-600]="!myObjectives.loading()">
                {{ myObjectives.loading() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">error:</span>
              <span class="font-mono text-[9px]" [class.text-red-600]="myObjectives.error()">
                {{ myObjectives.error() || 'null' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">lastUpdated:</span>
              <span class="font-mono text-[9px]">
                {{ myObjectives.lastUpdated() ? (myObjectives.lastUpdated() | date:'short') : 'null' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">lastSync:</span>
              <span class="font-mono text-[9px]">
                {{ myObjectives.lastSync() ? (myObjectives.lastSync() | date:'short') : 'null' }}
              </span>
            </div>
          </div>
        </div>

        <!-- Context Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Context</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">activeTeamId:</span>
              <span class="font-mono text-[9px]">{{ myObjectives.activeTeamId() || 'null' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">caseload:</span>
              <span class="font-mono text-[9px]">{{ myObjectives.caseload().length > 0 ? '[' +
                myObjectives.caseload().length + ' ids]' : '[]' }}</span>
            </div>
          </div>
        </div>

        <!-- Computed State Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Computed State</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">isReady:</span>
              <span class="font-medium" [class.text-success]="myObjectives.isReady()"
                [class.text-gray-600]="!myObjectives.isReady()">
                {{ myObjectives.isReady() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">hasData:</span>
              <span class="font-medium" [class.text-success]="myObjectives.hasData()"
                [class.text-gray-600]="!myObjectives.hasData()">
                {{ myObjectives.hasData() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">count:</span>
              <span class="font-medium">{{ myObjectives.count() }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">activeObjectivesCount:</span>
              <span class="font-medium text-success">{{ myObjectives.activeObjectivesCount() }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">completedObjectivesCount:</span>
              <span class="font-medium text-gray-600">{{ myObjectives.completedObjectivesCount() }}</span>
            </div>
          </div>
        </div>

        <!-- Data Signal -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Data Signal</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">data:</span>
              <span class="font-mono text-[9px]">{{ myObjectives.data().length > 0 ? '[' + myObjectives.data().length +
                ' objectives]' : '[]' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Objectives List (if any) -->
      @if (myObjectives.hasData()) {
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">
          Objectives ({{ myObjectives.count() }})
        </h4>

        <!-- Active vs Completed Summary -->
        <div class="flex gap-2">
          <div class="flex-1 p-2 bg-green-50 border border-green-200 rounded text-center">
            <div class="text-lg font-bold text-green-700">{{ myObjectives.activeObjectivesCount() }}</div>
            <div class="text-[10px] text-success">Active</div>
          </div>
          <div class="flex-1 p-2 bg-blue-50 border border-blue-200 rounded text-center">
            <div class="text-lg font-bold text-blue-700">{{ myObjectives.completedObjectivesCount() }}</div>
            <div class="text-[10px] text-blue-600">Completed</div>
          </div>
        </div>

        <div class="space-y-2 max-h-64 overflow-y-auto">
          @for (objective of myObjectives.objectivesSorted(); track objective.id) {
          <div class="p-2 bg-surface rounded border border-outline-variant" [class.opacity-50]="objective.completed">
            <div class="flex items-center justify-between mb-1">
              <div class="text-xs font-medium">
                {{ objective.skillId }}
              </div>
              @if (objective.completed) {
              <span class="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[9px]">✓ Completed</span>
              }
            </div>
            <div class="text-[10px]-variant space-y-0.5">
              <div>Student: {{ objective.studentId }}</div>
              @if (objective.goalId) {
              <div>Goal: {{ objective.goalId }}</div>
              }
              <div>Measurement: {{ objective.measurmentType }}</div>
              <div>Baseline: {{ objective.baselineValue }} → Target: {{ objective.targetValue }}</div>
              @if (objective.targetDate) {
              <div>Target: {{ objective.targetDate | date:'short' }}</div>
              }
              <div class="font-mono text-[9px]-variant/70 mt-1">
                ID: {{ objective.id }}
              </div>
            </div>
          </div>
          }
        </div>
      </div>
      } @else if (myObjectives.initialized() && !myObjectives.loading()) {
      <div class="p-3 bg-surface-variant rounded text-center text-xs-variant">
        No objectives for students in caseload
      </div>
      }

      <!-- Raw Data (Collapsible) -->
      <details class="space-y-2">
        <summary class="text-xs font-medium-variant cursor-pointer">
          Raw Data
        </summary>
        <pre class="p-2 bg-surface rounded text-[10px] overflow-x-auto">{{
          myObjectives.data() | json
        }}</pre>
      </details>
    </div>
    }
  </div>

  <!-- ========== MY REMINDERS STATE ========== -->
  <div class="space-y-3 bg-surface rounded-lg border border-outline-variant select-none overflow-hidden"
    [class.pb-4]="myRemindersExpanded()">
    <div
      class="flex items-center bg-surface clickable justify-between text-lg font-semibold px-3 py-4 cursor-pointer"
      [class.border-b]="myRemindersExpanded()" (click)="toggleMyRemindersExpanded()">
      <span>🔔 My Reminders State (Team-Scoped)</span>
      <button class="p-2 hover:bg-surface-variant rounded transition-colors">
        @if (myRemindersExpanded()) {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
        } @else {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
        }
      </button>
    </div>

    <!-- Collapsible Content -->
    @if (myRemindersExpanded()) {
    <div class="space-y-3 pb-4 px-3">
      <!-- Status Indicators -->
      <app-state-status-indicators [initialized]="myReminders.initialized()" [loading]="myReminders.loading()"
        [isReady]="myReminders.isReady()" [hasData]="myReminders.hasData()" [error]="myReminders.error()" />

      <!-- State Section - All Public Signals -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">📊 State (All Public Signals)</h4>

        <!-- Basic State Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Basic State</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">initialized:</span>
              <span class="font-medium" [class.text-success]="myReminders.initialized()"
                [class.text-gray-600]="!myReminders.initialized()">
                {{ myReminders.initialized() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">loading:</span>
              <span class="font-medium" [class.text-yellow-600]="myReminders.loading()"
                [class.text-gray-600]="!myReminders.loading()">
                {{ myReminders.loading() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">error:</span>
              <span class="font-mono text-[9px]" [class.text-red-600]="myReminders.error()">
                {{ myReminders.error() || 'null' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">lastUpdated:</span>
              <span class="font-mono text-[9px]">
                {{ myReminders.lastUpdated() ? (myReminders.lastUpdated() | date:'short') : 'null' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">lastSync:</span>
              <span class="font-mono text-[9px]">
                {{ myReminders.lastSync() ? (myReminders.lastSync() | date:'short') : 'null' }}
              </span>
            </div>
          </div>
        </div>

        <!-- Context Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Context</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">activeTeamId:</span>
              <span class="font-mono text-[9px]">{{ myReminders.activeTeamId() || 'null' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">caseload:</span>
              <span class="font-mono text-[9px]">{{ myReminders.caseload().length > 0 ? '[' +
                myReminders.caseload().length + ' ids]' : '[]' }}</span>
            </div>
          </div>
        </div>

        <!-- Computed State Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Computed State</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">isReady:</span>
              <span class="font-medium" [class.text-success]="myReminders.isReady()"
                [class.text-gray-600]="!myReminders.isReady()">
                {{ myReminders.isReady() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">hasData:</span>
              <span class="font-medium" [class.text-success]="myReminders.hasData()"
                [class.text-gray-600]="!myReminders.hasData()">
                {{ myReminders.hasData() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">count:</span>
              <span class="font-medium">{{ myReminders.count() }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">observationDueCount:</span>
              <span class="font-medium text-orange-600">{{ myReminders.observationDueCount() }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">goalDueCount:</span>
              <span class="font-medium text-purple-600">{{ myReminders.goalDueCount() }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">upcomingCount:</span>
              <span class="font-medium text-blue-600">{{ myReminders.upcomingCount() }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">overdueCount:</span>
              <span class="font-medium text-red-600">{{ myReminders.overdueCount() }}</span>
            </div>
          </div>
        </div>

        <!-- Data Signal -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Data Signal</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">data:</span>
              <span class="font-mono text-[9px]">{{ myReminders.data().length > 0 ? '[' + myReminders.data().length + '
                reminders]' : '[]' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Reminders Summary (if any) -->
      @if (myReminders.hasData()) {
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">
          Reminders ({{ myReminders.count() }})
        </h4>

        <!-- Type Summary Cards -->
        <div class="flex gap-2">
          <div class="flex-1 p-2 bg-orange-50 border border-orange-200 rounded text-center">
            <div class="text-lg font-bold text-orange-700">{{ myReminders.observationDueCount() }}</div>
            <div class="text-[10px] text-orange-600">Observation</div>
          </div>
          <div class="flex-1 p-2 bg-purple-50 border border-purple-200 rounded text-center">
            <div class="text-lg font-bold text-purple-700">{{ myReminders.goalDueCount() }}</div>
            <div class="text-[10px] text-purple-600">Goal Due</div>
          </div>
        </div>

        <!-- Status Summary Cards -->
        <div class="flex gap-2">
          <div class="flex-1 p-2 bg-blue-50 border border-blue-200 rounded text-center">
            <div class="text-lg font-bold text-blue-700">{{ myReminders.upcomingCount() }}</div>
            <div class="text-[10px] text-blue-600">Upcoming</div>
          </div>
          <div class="flex-1 p-2 bg-red-50 border border-red-200 rounded text-center">
            <div class="text-lg font-bold text-red-700">{{ myReminders.overdueCount() }}</div>
            <div class="text-[10px] text-red-600">Overdue</div>
          </div>
        </div>

        <div class="space-y-2 max-h-64 overflow-y-auto">
          @for (reminder of myReminders.remindersSorted(); track reminder.id) {
          <div class="p-2 bg-surface rounded border border-outline-variant"
            [class.border-red-300]="myReminders.overdueReminders().includes(reminder)"
            [class.bg-red-50]="myReminders.overdueReminders().includes(reminder)">
            <div class="flex items-center justify-between mb-1">
              <div class="text-xs font-medium">
                {{ reminder.date | date:'MMM d, y' }}
              </div>
              <span class="px-2 py-0.5 rounded-full text-[9px] font-medium"
                [class.bg-orange-100]="reminder.type === 'OBSERVATION_DUE'"
                [class.text-orange-700]="reminder.type === 'OBSERVATION_DUE'"
                [class.bg-purple-100]="reminder.type === 'GOAL_DUE'"
                [class.text-purple-700]="reminder.type === 'GOAL_DUE'">
                {{ reminder.type === 'OBSERVATION_DUE' ? 'Observation' : 'Goal Due' }}
              </span>
            </div>
            <div class="text-[10px]-variant space-y-0.5">
              <div>Student: {{ reminder.studentId }}</div>
              <div>Goal: {{ reminder.goalId }}</div>
              <div>Skill Area: {{ reminder.majorSkillAreaId }}</div>
              @if (myReminders.overdueReminders().includes(reminder)) {
              <div class="text-red-600 font-medium mt-1">⚠ Overdue</div>
              }
              <div class="font-mono text-[9px]-variant/70 mt-1">
                ID: {{ reminder.id }}
              </div>
            </div>
          </div>
          }
        </div>
      </div>
      } @else if (myReminders.initialized() && !myReminders.loading()) {
      <div class="p-3 bg-surface-variant rounded text-center text-xs-variant">
        No reminders for students in caseload
      </div>
      }

      <!-- Raw Data (Collapsible) -->
      <details class="space-y-2">
        <summary class="text-xs font-medium-variant cursor-pointer">
          Raw Data
        </summary>
        <pre class="p-2 bg-surface rounded text-[10px] overflow-x-auto">{{
          myReminders.data() | json
        }}</pre>
      </details>
    </div>
    }
  </div>

  <!-- ============================================ -->
  <!-- TEAM STUDENTS STATE (Team-Scoped, Real-time, Cached) -->
  <!-- ============================================ -->
  <div class="space-y-3 bg-surface rounded-lg border border-outline-variant select-none overflow-hidden"
    [class.pb-4]="studentsExpanded()">
    <div
      class="flex items-center bg-surface clickable justify-between text-lg font-semibold px-3 py-4 cursor-pointer"
      [class.border-b]="studentsExpanded()" (click)="toggleStudentsExpanded()">
      <span>👥 Team Students State (Team-Scoped, Real-time, Cached)</span>
      <button class="p-2 hover:bg-surface-variant rounded transition-colors">
        @if (studentsExpanded()) {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
        } @else {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
        }
      </button>
    </div>

    <!-- Collapsible Content -->
    @if (studentsExpanded()) {
    <div class="space-y-3 pb-4 px-3">
      <!-- Status Indicators -->
      <app-state-status-indicators [initialized]="students.initialized()" [loading]="students.loading()"
        [isReady]="students.isReady()" [hasData]="students.hasData()" [error]="students.error()" />

      <!-- State Section - All Public Signals -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">📊 State (All Public Signals)</h4>

        <!-- Basic State Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Basic State</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">initialized:</span>
              <span class="font-medium" [class.text-success]="students.initialized()"
                [class.text-gray-600]="!students.initialized()">
                {{ students.initialized() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">loading:</span>
              <span class="font-medium" [class.text-yellow-600]="students.loading()"
                [class.text-gray-600]="!students.loading()">
                {{ students.loading() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">error:</span>
              <span class="font-mono text-[9px]" [class.text-red-600]="students.error()">
                {{ students.error() || 'null' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">lastUpdated:</span>
              <span class="font-mono text-[9px]">
                {{ students.lastUpdated() ? (students.lastUpdated() | date:'short') : 'null' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">lastSync:</span>
              <span class="font-mono text-[9px]">
                {{ students.lastSync() ? (students.lastSync() | date:'short') : 'null' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">hydratedFromCache:</span>
              <span class="font-medium" [class.text-blue-600]="students.hydratedFromCache()"
                [class.text-gray-600]="!students.hydratedFromCache()">
                {{ students.hydratedFromCache() }}
              </span>
            </div>
          </div>
        </div>

        <!-- Context Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Context</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">activeTeamId:</span>
              <span class="font-mono text-[9px]">{{ students.activeTeamId() || 'null' }}</span>
            </div>
          </div>
        </div>

        <!-- Computed State Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Computed State</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">isReady:</span>
              <span class="font-medium" [class.text-success]="students.isReady()"
                [class.text-gray-600]="!students.isReady()">
                {{ students.isReady() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">hasData:</span>
              <span class="font-medium" [class.text-success]="students.hasData()"
                [class.text-gray-600]="!students.hasData()">
                {{ students.hasData() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">count:</span>
              <span class="font-medium">{{ students.count() }}</span>
            </div>
          </div>
        </div>

        <!-- Data Signal -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Data Signal</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">data:</span>
              <span class="font-mono text-[9px]">{{ students.data().length > 0 ? '[' + students.data().length + '
                students]' : '[]' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Students Summary (if any) -->
      @if (students.hasData()) {
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">
          Students ({{ students.count() }})
        </h4>

        <!-- Cache Diagnostics -->
        @let studentsSizeWarning = getCacheSizeWarning('students', students.count());
        <div class="p-3 rounded border"
          [class.bg-green-50]="students.hydratedFromCache() && !studentsSizeWarning"
          [class.border-green-300]="students.hydratedFromCache() && !studentsSizeWarning"
          [class.bg-orange-50]="!students.hydratedFromCache() && !studentsSizeWarning"
          [class.border-orange-300]="!students.hydratedFromCache() && !studentsSizeWarning"
          [class.bg-red-50]="!!studentsSizeWarning"
          [class.border-red-300]="!!studentsSizeWarning">
          <div class="space-y-1 text-xs">
            <div class="flex justify-between items-center">
              <span class="font-semibold text-[11px]"
                [class.text-green-900]="students.hydratedFromCache() && !studentsSizeWarning"
                [class.text-orange-900]="!students.hydratedFromCache() && !studentsSizeWarning"
                [class.text-red-900]="!!studentsSizeWarning">
                🔍 Cache Diagnostics
              </span>
              <span class="font-bold text-[11px] px-1.5 py-0.5 rounded"
                [class.bg-green-200]="students.hydratedFromCache() && !studentsSizeWarning"
                [class.text-green-800]="students.hydratedFromCache() && !studentsSizeWarning"
                [class.bg-orange-200]="!students.hydratedFromCache() && !studentsSizeWarning"
                [class.text-orange-800]="!students.hydratedFromCache() && !studentsSizeWarning"
                [class.bg-red-200]="!!studentsSizeWarning"
                [class.text-red-800]="!!studentsSizeWarning">
                {{ studentsSizeWarning ? '🔴 Too Large' : students.hydratedFromCache() ? '🟢 Cache Hit' : '🔴 Firestore Load' }}
              </span>
            </div>
            @if (studentsSizeWarning) {
              <div class="text-[10px] text-red-700 font-medium">
                {{ studentsSizeWarning }} — requires IndexedDB
              </div>
            }
            <div class="flex justify-between">
              <span class="text-on-surface-variant">Last Sync:</span>
              <span class="font-mono text-[10px]">
                {{ students.lastSync() ? (students.lastSync() | date:'short') : 'Never' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant">Sync Age:</span>
              <span class="font-mono text-[10px]">{{ getCacheAgeMins(students.lastSync()) }}</span>
            </div>
            <div class="pt-1 border-t border-outline-variant">
              <div class="text-on-surface-variant mb-0.5">localStorage key:</div>
              <div class="font-mono text-[9px] break-all text-on-surface">
                {{ getCacheKey('students', students.activeTeamId()) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Sample Students (first 5) -->
        <div class="p-2 bg-surface-variant rounded max-h-40 overflow-y-auto">
          <h5 class="text-[10px] font-semibold-variant mb-2">
            Sample (first 5 of {{ students.count() }})
          </h5>
          @for (student of students.studentsSorted().slice(0, 5); track student.id || student.studentId) {
          <div class="text-[10px] py-1 border-b border-outline-variant last:border-0">
            <div class="font-semibold">{{ student.lastName }}, {{ student.firstName }}</div>
            <div class="text-on-surface-variant">
              Grade: {{ student.grade || 'N/A' }} | School: {{ student.school || 'N/A' }}
            </div>
          </div>
          }
        </div>
      </div>
      }

      <!-- Raw Data Toggle -->
      <details class="text-xs">
        <summary
          class="cursor-pointer font-medium-variant hover:text-on-surface p-2 bg-surface-variant rounded">
          View Raw Data ({{ students.count() }} students)
        </summary>
        <pre class="p-2 bg-surface rounded text-[10px] overflow-x-auto">{{
          students.data() | json
        }}</pre>
      </details>
    </div>
    }
  </div>

  <!-- ============================================ -->
  <!-- TEAM USERS STATE (Team-Scoped, Real-time, Cached) -->
  <!-- ============================================ -->
  <div class="space-y-3 bg-surface rounded-lg border border-outline-variant select-none overflow-hidden"
    [class.pb-4]="usersExpanded()">
    <div
      class="flex items-center bg-surface clickable justify-between text-lg font-semibold px-3 py-4 cursor-pointer"
      [class.border-b]="usersExpanded()" (click)="toggleUsersExpanded()">
      <span>👥 Team Users State (Team-Scoped, Real-time, Cached)</span>
      <button class="p-2 hover:bg-surface-variant rounded transition-colors">
        @if (usersExpanded()) {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
        } @else {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
        }
      </button>
    </div>

    <!-- Collapsible Content -->
    @if (usersExpanded()) {
    <div class="space-y-3 pb-4 px-3">
      <!-- Status Indicators -->
      <app-state-status-indicators [initialized]="users.initialized()" [loading]="users.loading()"
        [isReady]="users.isReady()" [hasData]="users.hasData()" [error]="users.error()" />

      <!-- State Section - All Public Signals -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">📊 State (All Public Signals)</h4>

        <!-- Basic State Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Basic State</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">initialized:</span>
              <span class="font-medium" [class.text-success]="users.initialized()"
                [class.text-gray-600]="!users.initialized()">
                {{ users.initialized() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">loading:</span>
              <span class="font-medium" [class.text-yellow-600]="users.loading()"
                [class.text-gray-600]="!users.loading()">
                {{ users.loading() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">error:</span>
              <span class="font-mono text-[9px]" [class.text-red-600]="users.error()">
                {{ users.error() || 'null' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">lastUpdated:</span>
              <span class="font-mono text-[9px]">
                {{ users.lastUpdated() ? (users.lastUpdated() | date:'short') : 'null' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">lastSync:</span>
              <span class="font-mono text-[9px]">
                {{ users.lastSync() ? (users.lastSync() | date:'short') : 'null' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">hydratedFromCache:</span>
              <span class="font-medium" [class.text-blue-600]="users.hydratedFromCache()"
                [class.text-gray-600]="!users.hydratedFromCache()">
                {{ users.hydratedFromCache() }}
              </span>
            </div>
          </div>
        </div>

        <!-- Context Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Context</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">activeTeamId:</span>
              <span class="font-mono text-[9px]">{{ users.activeTeamId() || 'null' }}</span>
            </div>
          </div>
        </div>

        <!-- Computed State Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Computed State</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">isReady:</span>
              <span class="font-medium" [class.text-success]="users.isReady()"
                [class.text-gray-600]="!users.isReady()">
                {{ users.isReady() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">hasData:</span>
              <span class="font-medium" [class.text-success]="users.hasData()"
                [class.text-gray-600]="!users.hasData()">
                {{ users.hasData() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">count:</span>
              <span class="font-medium">{{ users.count() }}</span>
            </div>
          </div>
        </div>

        <!-- Data Signal -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Data Signal</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">data:</span>
              <span class="font-mono text-[9px]">{{ users.data().length > 0 ? '[' + users.data().length + '
                users]' : '[]' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Users Summary (if any) -->
      @if (users.hasData()) {
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">
          Users ({{ users.count() }})
        </h4>

        <!-- Cache Diagnostics -->
        @let usersSizeWarning = getCacheSizeWarning('users', users.count());
        <div class="p-3 rounded border"
          [class.bg-green-50]="users.hydratedFromCache() && !usersSizeWarning"
          [class.border-green-300]="users.hydratedFromCache() && !usersSizeWarning"
          [class.bg-orange-50]="!users.hydratedFromCache() && !usersSizeWarning"
          [class.border-orange-300]="!users.hydratedFromCache() && !usersSizeWarning"
          [class.bg-red-50]="!!usersSizeWarning"
          [class.border-red-300]="!!usersSizeWarning">
          <div class="space-y-1 text-xs">
            <div class="flex justify-between items-center">
              <span class="font-semibold text-[11px]"
                [class.text-green-900]="users.hydratedFromCache() && !usersSizeWarning"
                [class.text-orange-900]="!users.hydratedFromCache() && !usersSizeWarning"
                [class.text-red-900]="!!usersSizeWarning">
                🔍 Cache Diagnostics
              </span>
              <span class="font-bold text-[11px] px-1.5 py-0.5 rounded"
                [class.bg-green-200]="users.hydratedFromCache() && !usersSizeWarning"
                [class.text-green-800]="users.hydratedFromCache() && !usersSizeWarning"
                [class.bg-orange-200]="!users.hydratedFromCache() && !usersSizeWarning"
                [class.text-orange-800]="!users.hydratedFromCache() && !usersSizeWarning"
                [class.bg-red-200]="!!usersSizeWarning"
                [class.text-red-800]="!!usersSizeWarning">
                {{ usersSizeWarning ? '🔴 Too Large' : users.hydratedFromCache() ? '🟢 Cache Hit' : '🔴 Firestore Load' }}
              </span>
            </div>
            @if (usersSizeWarning) {
              <div class="text-[10px] text-red-700 font-medium">
                {{ usersSizeWarning }} — requires IndexedDB
              </div>
            }
            <div class="flex justify-between">
              <span class="text-on-surface-variant">Last Sync:</span>
              <span class="font-mono text-[10px]">
                {{ users.lastSync() ? (users.lastSync() | date:'short') : 'Never' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant">Sync Age:</span>
              <span class="font-mono text-[10px]">{{ getCacheAgeMins(users.lastSync()) }}</span>
            </div>
            <div class="pt-1 border-t border-outline-variant">
              <div class="text-on-surface-variant mb-0.5">localStorage key:</div>
              <div class="font-mono text-[9px] break-all text-on-surface">
                {{ getCacheKey('users', users.activeTeamId()) }}
              </div>
            </div>
          </div>
        </div>

        <!-- User Stats -->
        <div class="grid grid-cols-2 gap-2">
          <div class="p-2 bg-green-50 border border-green-200 rounded text-xs">
            <strong>{{ users.activeUsers().length }}</strong> Active
          </div>
          <div class="p-2 bg-red-50 border border-red-200 rounded text-xs">
            <strong>{{ users.deactivatedUsers().length }}</strong> Deactivated
          </div>
          <div class="p-2 bg-blue-50 border border-blue-200 rounded text-xs">
            <strong>{{ users.invitedUsers().length }}</strong> Accepted
          </div>
          <div class="p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
            <strong>{{ users.pendingUsers().length }}</strong> Pending
          </div>
        </div>

        <!-- Sample Users (first 5) -->
        <div class="p-2 bg-surface-variant rounded max-h-40 overflow-y-auto">
          <h5 class="text-[10px] font-semibold-variant mb-2">
            Sample (first 5 of {{ users.count() }})
          </h5>
          @for (user of users.usersSorted().slice(0, 5); track user.id || user.email) {
          <div class="text-[10px] py-1 border-b border-outline-variant last:border-0">
            <div class="font-semibold">{{ user.lastName || 'N/A' }}, {{ user.firstName || 'N/A' }}</div>
            <div class="text-on-surface-variant">
              Email: {{ user.email || 'N/A' }} | Role: {{ user.role || 'N/A' }}
            </div>
            <div class="text-[9px]" [class.text-success]="!user.deactivated" [class.text-red-600]="user.deactivated">
              {{ user.deactivated ? 'Deactivated' : 'Active' }}
              {{ user.inviteAccepted ? '| Invite Accepted' : '| Invite Pending' }}
            </div>
          </div>
          }
        </div>
      </div>
      }

      <!-- Raw Data Toggle -->
      <details class="text-xs">
        <summary
          class="cursor-pointer font-medium-variant hover:text-on-surface p-2 bg-surface-variant rounded">
          View Raw Data ({{ users.count() }} users)
        </summary>
        <pre class="p-2 bg-surface rounded text-[10px] overflow-x-auto">{{
          users.data() | json
        }}</pre>
      </details>
    </div>
    }
  </div>

  <!-- ============================================ -->
  <!-- ADMIN GOALS STATE (Admin-Only, Real-time, Cached) -->
  <!-- ============================================ -->
  <div class="space-y-3 bg-surface rounded-lg border border-outline-variant select-none overflow-hidden"
    [class.pb-4]="adminGoalsExpanded()">
    <div
      class="flex items-center bg-surface clickable justify-between text-lg font-semibold px-3 py-4 cursor-pointer"
      [class.border-b]="adminGoalsExpanded()" (click)="toggleAdminGoalsExpanded()">
      <span>🎯 Admin Goals State (Admin-Only, Real-time, Cached)</span>
      <button class="p-2 hover:bg-surface-variant rounded transition-colors">
        @if (adminGoalsExpanded()) {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
        } @else {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
        }
      </button>
    </div>

    <!-- Collapsible Content -->
    @if (adminGoalsExpanded()) {
    <div class="space-y-3 pb-4 px-3">
      <!-- Status Indicators -->
      <app-state-status-indicators [initialized]="adminGoals.initialized()" [loading]="adminGoals.loading()"
        [isReady]="adminGoals.isReady()" [hasData]="adminGoals.hasData()" [error]="adminGoals.error()" />

      <!-- State Section - All Public Signals -->
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">📊 State (All Public Signals)</h4>

        <!-- Basic State Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Basic State</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">initialized:</span>
              <span class="font-medium" [class.text-success]="adminGoals.initialized()"
                [class.text-gray-600]="!adminGoals.initialized()">
                {{ adminGoals.initialized() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">loading:</span>
              <span class="font-medium" [class.text-yellow-600]="adminGoals.loading()"
                [class.text-gray-600]="!adminGoals.loading()">
                {{ adminGoals.loading() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">error:</span>
              <span class="font-mono text-[9px]" [class.text-red-600]="adminGoals.error()">
                {{ adminGoals.error() || 'null' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">lastUpdated:</span>
              <span class="font-mono text-[9px]">
                {{ adminGoals.lastUpdated() ? (adminGoals.lastUpdated() | date:'short') : 'null' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">lastSync:</span>
              <span class="font-mono text-[9px]">
                {{ adminGoals.lastSync() ? (adminGoals.lastSync() | date:'short') : 'null' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">hydratedFromCache:</span>
              <span class="font-medium" [class.text-blue-600]="adminGoals.hydratedFromCache()"
                [class.text-gray-600]="!adminGoals.hydratedFromCache()">
                {{ adminGoals.hydratedFromCache() }}
              </span>
            </div>
          </div>
        </div>

        <!-- Context Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Context</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">activeTeamId:</span>
              <span class="font-mono text-[9px]">{{ adminGoals.activeTeamId() || 'null' }}</span>
            </div>
          </div>
        </div>

        <!-- Computed State Signals -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Computed State</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">isReady:</span>
              <span class="font-medium" [class.text-success]="adminGoals.isReady()"
                [class.text-gray-600]="!adminGoals.isReady()">
                {{ adminGoals.isReady() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">hasData:</span>
              <span class="font-medium" [class.text-success]="adminGoals.hasData()"
                [class.text-gray-600]="!adminGoals.hasData()">
                {{ adminGoals.hasData() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">count:</span>
              <span class="font-medium">{{ adminGoals.count() }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">activeGoalCount:</span>
              <span class="font-medium text-success">{{ adminGoals.activeGoalCount() }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">archivedGoalCount:</span>
              <span class="font-medium text-on-surface-variant">{{ adminGoals.archivedGoalCount() }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">studentsWithGoals:</span>
              <span class="font-medium">{{ adminGoals.studentsWithGoalsCount() }}</span>
            </div>
          </div>
        </div>

        <!-- Data Signal -->
        <div class="p-2 bg-surface-variant rounded">
          <h5 class="text-[10px] font-semibold-variant mb-2">Data Signal</h5>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-on-surface-variant font-mono text-[10px]">data:</span>
              <span class="font-mono text-[9px]">{{ adminGoals.data().length > 0 ? '[' + adminGoals.data().length + ' goals]' : '[]' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Goals Summary (if any) -->
      @if (adminGoals.hasData()) {
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">
          Goals ({{ adminGoals.count() }})
        </h4>

        <!-- Cache Diagnostics -->
        @let goalsSizeWarning = getCacheSizeWarning('goals', adminGoals.count());
        <div class="p-3 rounded border"
          [class.bg-green-50]="adminGoals.hydratedFromCache() && !goalsSizeWarning"
          [class.border-green-300]="adminGoals.hydratedFromCache() && !goalsSizeWarning"
          [class.bg-orange-50]="!adminGoals.hydratedFromCache() && !goalsSizeWarning"
          [class.border-orange-300]="!adminGoals.hydratedFromCache() && !goalsSizeWarning"
          [class.bg-red-50]="!!goalsSizeWarning"
          [class.border-red-300]="!!goalsSizeWarning">
          <div class="space-y-1 text-xs">
            <div class="flex justify-between items-center">
              <span class="font-semibold text-[11px]"
                [class.text-green-900]="adminGoals.hydratedFromCache() && !goalsSizeWarning"
                [class.text-orange-900]="!adminGoals.hydratedFromCache() && !goalsSizeWarning"
                [class.text-red-900]="!!goalsSizeWarning">
                🔍 Cache Diagnostics
              </span>
              <span class="font-bold text-[11px] px-1.5 py-0.5 rounded"
                [class.bg-green-200]="adminGoals.hydratedFromCache() && !goalsSizeWarning"
                [class.text-green-800]="adminGoals.hydratedFromCache() && !goalsSizeWarning"
                [class.bg-orange-200]="!adminGoals.hydratedFromCache() && !goalsSizeWarning"
                [class.text-orange-800]="!adminGoals.hydratedFromCache() && !goalsSizeWarning"
                [class.bg-red-200]="!!goalsSizeWarning"
                [class.text-red-800]="!!goalsSizeWarning">
                {{ goalsSizeWarning ? '🔴 Too Large' : adminGoals.hydratedFromCache() ? '🟢 Cache Hit' : '🔴 Firestore Load' }}
              </span>
            </div>
            @if (goalsSizeWarning) {
              <div class="text-[10px] text-red-700 font-medium">
                {{ goalsSizeWarning }} — requires IndexedDB
              </div>
            }
            <div class="flex justify-between">
              <span class="text-on-surface-variant">Last Sync:</span>
              <span class="font-mono text-[10px]">
                {{ adminGoals.lastSync() ? (adminGoals.lastSync() | date:'short') : 'Never' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-on-surface-variant">Sync Age:</span>
              <span class="font-mono text-[10px]">{{ getCacheAgeMins(adminGoals.lastSync()) }}</span>
            </div>
            <div class="pt-1 border-t border-outline-variant">
              <div class="text-on-surface-variant mb-0.5">localStorage key:</div>
              <div class="font-mono text-[9px] break-all text-on-surface">
                {{ getCacheKey('goals', adminGoals.activeTeamId()) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Sample Goals (first 5) -->
        <div class="p-2 bg-surface-variant rounded max-h-40 overflow-y-auto">
          <h5 class="text-[10px] font-semibold-variant mb-2">
            Sample (first 5 of {{ adminGoals.count() }})
          </h5>
          @for (goal of adminGoals.data().slice(0, 5); track goal.id) {
          <div class="text-[10px] py-1 border-b border-outline-variant last:border-0">
            <div class="font-semibold font-mono">{{ goal.id }}</div>
            <div class="text-on-surface-variant">
              Student: {{ goal.studentId }} | Skills: {{ goal.skillIds?.length ?? 0 }} | Archived: {{ goal.archived ?? false }}
            </div>
          </div>
          }
        </div>
      </div>
      }

      <!-- Raw Data Toggle -->
      <details class="text-xs">
        <summary
          class="cursor-pointer font-medium-variant hover:text-on-surface p-2 bg-surface-variant rounded">
          View Raw Data ({{ adminGoals.count() }} goals)
        </summary>
        <pre class="p-2 bg-surface rounded text-[10px] overflow-x-auto">{{
          adminGoals.data() | json
        }}</pre>
      </details>
    </div>
    }
  </div>

  <!-- ========== ADMIN MEASUREMENTS STATE (Admin-Only, Cached) ========== -->
  <div class="space-y-3 bg-surface rounded-lg border border-outline-variant select-none overflow-hidden"
    [class.pb-4]="adminMeasurementsExpanded()">
    <div
      class="flex items-center bg-surface clickable justify-between text-lg font-semibold px-3 py-4 cursor-pointer"
      [class.border-b]="adminMeasurementsExpanded()" (click)="toggleAdminMeasurementsExpanded()">
      <span>📏 Admin Measurements State (Admin-Only, Cached)</span>
      <button class="p-2 hover:bg-surface-variant rounded transition-colors">
        @if (adminMeasurementsExpanded()) {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
        } @else {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
        }
      </button>
    </div>

    @if (adminMeasurementsExpanded()) {
    <div class="space-y-3 pb-4 px-3">
      <app-state-status-indicators
        [initialized]="adminMeasurements.initialized()"
        [loading]="adminMeasurements.loading()"
        [isReady]="adminMeasurements.isReady()"
        [hasData]="adminMeasurements.hasData()"
        [error]="adminMeasurements.error()" />

      <!-- Basic State -->
      <div class="p-2 bg-surface-variant rounded">
        <h5 class="text-[10px] font-semibold-variant mb-2">Basic State</h5>
        <div class="space-y-1 text-xs">
          <div class="flex justify-between">
            <span class="text-on-surface-variant font-mono text-[10px]">initialized:</span>
            <span class="font-medium" [class.text-success]="adminMeasurements.initialized()"
              [class.text-gray-600]="!adminMeasurements.initialized()">
              {{ adminMeasurements.initialized() }}
            </span>
          </div>
          <div class="flex justify-between">
            <span class="text-on-surface-variant font-mono text-[10px]">loading:</span>
            <span class="font-medium" [class.text-yellow-600]="adminMeasurements.loading()"
              [class.text-gray-600]="!adminMeasurements.loading()">
              {{ adminMeasurements.loading() }}
            </span>
          </div>
          <div class="flex justify-between">
            <span class="text-on-surface-variant font-mono text-[10px]">error:</span>
            <span class="font-mono text-[9px]" [class.text-red-600]="adminMeasurements.error()">
              {{ adminMeasurements.error() || 'null' }}
            </span>
          </div>
          <div class="flex justify-between">
            <span class="text-on-surface-variant font-mono text-[10px]">hydratedFromCache:</span>
            <span class="font-medium" [class.text-blue-600]="adminMeasurements.hydratedFromCache()"
              [class.text-gray-600]="!adminMeasurements.hydratedFromCache()">
              {{ adminMeasurements.hydratedFromCache() }}
            </span>
          </div>
          <div class="flex justify-between">
            <span class="text-on-surface-variant font-mono text-[10px]">lastSync:</span>
            <span class="font-mono text-[9px]">
              {{ adminMeasurements.lastSync() ? (adminMeasurements.lastSync() | date:'short') : 'null' }}
            </span>
          </div>
        </div>
      </div>

      <!-- Computed State -->
      <div class="p-2 bg-surface-variant rounded">
        <h5 class="text-[10px] font-semibold-variant mb-2">Computed State</h5>
        <div class="space-y-1 text-xs">
          <div class="flex justify-between">
            <span class="text-on-surface-variant font-mono text-[10px]">count:</span>
            <span class="font-medium">{{ adminMeasurements.count() }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-on-surface-variant font-mono text-[10px]">studentCount:</span>
            <span class="font-medium">{{ adminMeasurements.studentCount() }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-on-surface-variant font-mono text-[10px]">activeTeamId:</span>
            <span class="font-mono text-[9px]">{{ adminMeasurements.activeTeamId() || 'null' }}</span>
          </div>
        </div>
      </div>

      <!-- Cache Diagnostics -->
      @let measurementsSizeWarning = getCacheSizeWarning('measurements', adminMeasurements.count());
      <div class="p-3 rounded border"
        [class.bg-green-50]="adminMeasurements.hydratedFromCache() && !measurementsSizeWarning"
        [class.border-green-300]="adminMeasurements.hydratedFromCache() && !measurementsSizeWarning"
        [class.bg-orange-50]="!adminMeasurements.hydratedFromCache() && !measurementsSizeWarning"
        [class.border-orange-300]="!adminMeasurements.hydratedFromCache() && !measurementsSizeWarning"
        [class.bg-red-50]="!!measurementsSizeWarning"
        [class.border-red-300]="!!measurementsSizeWarning">
        <div class="space-y-1 text-xs">
          <div class="flex justify-between items-center">
            <span class="font-semibold text-[11px]"
              [class.text-green-900]="adminMeasurements.hydratedFromCache() && !measurementsSizeWarning"
              [class.text-orange-900]="!adminMeasurements.hydratedFromCache() && !measurementsSizeWarning"
              [class.text-red-900]="!!measurementsSizeWarning">
              🔍 Cache Diagnostics
            </span>
            <span class="font-bold text-[11px] px-1.5 py-0.5 rounded"
              [class.bg-green-200]="adminMeasurements.hydratedFromCache() && !measurementsSizeWarning"
              [class.text-green-800]="adminMeasurements.hydratedFromCache() && !measurementsSizeWarning"
              [class.bg-orange-200]="!adminMeasurements.hydratedFromCache() && !measurementsSizeWarning"
              [class.text-orange-800]="!adminMeasurements.hydratedFromCache() && !measurementsSizeWarning"
              [class.bg-red-200]="!!measurementsSizeWarning"
              [class.text-red-800]="!!measurementsSizeWarning">
              {{ measurementsSizeWarning ? '🔴 Too Large' : adminMeasurements.hydratedFromCache() ? '🟢 Cache Hit' : '🔴 Firestore Load' }}
            </span>
          </div>
          @if (measurementsSizeWarning) {
            <div class="text-[10px] text-red-700 font-medium">
              {{ measurementsSizeWarning }} — requires IndexedDB
            </div>
          }
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Sync Age:</span>
            <span class="font-mono text-[10px]">{{ getCacheAgeMins(adminMeasurements.lastSync()) }}</span>
          </div>
          <div class="pt-1 border-t border-outline-variant">
            <div class="text-on-surface-variant mb-0.5">localStorage key:</div>
            <div class="font-mono text-[9px] break-all text-on-surface">
              {{ getCacheKey('measurements', adminMeasurements.activeTeamId()) }}
            </div>
          </div>
        </div>
      </div>

      <!-- Raw Data Toggle -->
      <details class="text-xs">
        <summary class="cursor-pointer font-medium-variant hover:text-on-surface p-2 bg-surface-variant rounded">
          View Raw Data ({{ adminMeasurements.count() }} measurements)
        </summary>
        <pre class="p-2 bg-surface rounded text-[10px] overflow-x-auto max-h-60 overflow-y-auto">{{
          adminMeasurements.data().slice(0, 20) | json
        }}</pre>
      </details>
    </div>
    }
  </div>

  <!-- ========== ADMIN OBSERVATIONS STATE (Admin-Only, Cached) ========== -->
  <div class="space-y-3 bg-surface rounded-lg border border-outline-variant select-none overflow-hidden"
    [class.pb-4]="adminObservationsExpanded()">
    <div
      class="flex items-center bg-surface clickable justify-between text-lg font-semibold px-3 py-4 cursor-pointer"
      [class.border-b]="adminObservationsExpanded()" (click)="toggleAdminObservationsExpanded()">
      <span>👁️ Admin Observations State (Admin-Only, Cached)</span>
      <button class="p-2 hover:bg-surface-variant rounded transition-colors">
        @if (adminObservationsExpanded()) {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
        } @else {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
        }
      </button>
    </div>
    @if (adminObservationsExpanded()) {
    <div class="px-3 space-y-3">

      <!-- Status Indicators -->
      <app-state-status-indicators
        [initialized]="adminObservations.initialized()"
        [loading]="adminObservations.loading()"
        [isReady]="adminObservations.isReady()"
        [hasData]="adminObservations.hasData()"
        [error]="adminObservations.error()" />

      <!-- Basic State -->
      <div class="space-y-1 text-xs">
        <div class="flex justify-between">
          <span class="text-on-surface-variant">Initialized:</span>
          <span class="font-medium" [class.text-success]="adminObservations.initialized()"
            [class.text-gray-600]="!adminObservations.initialized()">
            {{ adminObservations.initialized() }}
          </span>
        </div>
        <div class="flex justify-between">
          <span class="text-on-surface-variant">Loading:</span>
          <span class="font-medium" [class.text-yellow-600]="adminObservations.loading()"
            [class.text-gray-600]="!adminObservations.loading()">
            {{ adminObservations.loading() }}
          </span>
        </div>
        <div class="flex justify-between">
          <span class="text-on-surface-variant">Error:</span>
          <span class="font-mono text-[9px]" [class.text-red-600]="adminObservations.error()">
            {{ adminObservations.error() || 'null' }}
          </span>
        </div>
        <div class="flex justify-between">
          <span class="text-on-surface-variant">Hydrated from Cache:</span>
          <span class="font-medium" [class.text-blue-600]="adminObservations.hydratedFromCache()"
            [class.text-gray-600]="!adminObservations.hydratedFromCache()">
            {{ adminObservations.hydratedFromCache() }}
          </span>
        </div>
        <div class="flex justify-between">
          <span class="text-on-surface-variant">Last Sync:</span>
          <span class="font-mono text-[10px]">
            {{ adminObservations.lastSync() ? (adminObservations.lastSync() | date:'short') : 'null' }}
          </span>
        </div>
      </div>

      <!-- Computed State -->
      <div class="space-y-1 text-xs">
        <div class="flex justify-between">
          <span class="text-on-surface-variant">Total Observations:</span>
          <span class="font-medium">{{ adminObservations.count() }}</span>
        </div>
        <div class="flex justify-between">
          <span class="text-on-surface-variant">Student Count:</span>
          <span class="font-medium">{{ adminObservations.studentCount() }}</span>
        </div>
        <div class="flex justify-between">
          <span class="text-on-surface-variant">Active Team ID:</span>
          <span class="font-mono text-[9px]">{{ adminObservations.activeTeamId() || 'null' }}</span>
        </div>
      </div>

      <!-- Cache Diagnostics -->
      @let observationsSizeWarning = getCacheSizeWarning('observations', adminObservations.count());
      <div class="p-3 rounded border"
        [class.bg-green-50]="adminObservations.hydratedFromCache() && !observationsSizeWarning"
        [class.border-green-300]="adminObservations.hydratedFromCache() && !observationsSizeWarning"
        [class.bg-orange-50]="!adminObservations.hydratedFromCache() && !observationsSizeWarning"
        [class.border-orange-300]="!adminObservations.hydratedFromCache() && !observationsSizeWarning"
        [class.bg-red-50]="!!observationsSizeWarning"
        [class.border-red-300]="!!observationsSizeWarning">
        <div class="space-y-1 text-xs">
          <div class="flex justify-between items-center">
            <span class="font-semibold text-[11px]"
              [class.text-green-900]="adminObservations.hydratedFromCache() && !observationsSizeWarning"
              [class.text-orange-900]="!adminObservations.hydratedFromCache() && !observationsSizeWarning"
              [class.text-red-900]="!!observationsSizeWarning">
              🔍 Cache Diagnostics
            </span>
            <span class="font-bold text-[11px] px-1.5 py-0.5 rounded"
              [class.bg-green-200]="adminObservations.hydratedFromCache() && !observationsSizeWarning"
              [class.text-green-800]="adminObservations.hydratedFromCache() && !observationsSizeWarning"
              [class.bg-orange-200]="!adminObservations.hydratedFromCache() && !observationsSizeWarning"
              [class.text-orange-800]="!adminObservations.hydratedFromCache() && !observationsSizeWarning"
              [class.bg-red-200]="!!observationsSizeWarning"
              [class.text-red-800]="!!observationsSizeWarning">
              {{ observationsSizeWarning ? '🔴 Too Large' : adminObservations.hydratedFromCache() ? '🟢 Cache Hit' : '🔴 Firestore Load' }}
            </span>
          </div>
          @if (observationsSizeWarning) {
            <div class="text-[10px] text-red-700 font-medium">
              {{ observationsSizeWarning }} — requires IndexedDB
            </div>
          }
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Sync Age:</span>
            <span class="font-mono text-[10px]">{{ getCacheAgeMins(adminObservations.lastSync()) }}</span>
          </div>
          <div class="pt-1 border-t border-outline-variant">
            <div class="text-on-surface-variant mb-0.5">localStorage key:</div>
            <div class="font-mono text-[9px] break-all text-on-surface">
              {{ getCacheKey('observations', adminObservations.activeTeamId()) }}
            </div>
          </div>
        </div>
      </div>

      <!-- Raw Data Toggle -->
      <details class="text-xs">
        <summary class="cursor-pointer font-medium-variant hover:text-on-surface p-2 bg-surface-variant rounded">
          View Raw Data ({{ adminObservations.count() }} observations)
        </summary>
        <pre class="p-2 bg-surface rounded text-[10px] overflow-x-auto max-h-60 overflow-y-auto">{{
          adminObservations.data().slice(0, 20) | json
        }}</pre>
      </details>
    </div>
    }
  </div>

  <!-- ========== SKILLS STATE (Hard-coded, No DB) ========== -->
  <div class="space-y-3 bg-surface rounded-lg border border-outline-variant select-none overflow-hidden"
    [class.pb-4]="skillsExpanded()">
    <div
      class="flex items-center bg-surface clickable justify-between text-lg font-semibold px-3 py-4 cursor-pointer"
      [class.border-b]="skillsExpanded()" (click)="toggleSkillsExpanded()">
      <span>📚 Skills State (Hard-coded)</span>
      <button class="p-2 hover:bg-surface-variant rounded transition-colors">
        @if (skillsExpanded()) {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
        } @else {
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
        }
      </button>
    </div>

    @if (skillsExpanded()) {
    <div class="space-y-3 pb-4 px-3">
      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">Status</h4>
        <div class="space-y-1 text-xs">
          <div class="flex justify-between">
            <span class="text-on-surface-variant">initialized:</span>
            <span class="font-medium" [class.text-success]="skills.initialized()">{{ skills.initialized() }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-on-surface-variant">isReady:</span>
            <span class="font-medium" [class.text-success]="skills.isReady()">{{ skills.isReady() }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Skills count:</span>
            <span class="font-medium">{{ skills.skills().length }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Categories:</span>
            <span class="font-medium">{{ skills.categories().length }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-on-surface-variant">Areas:</span>
            <span class="font-medium">{{ skills.areas().length }}</span>
          </div>
        </div>
      </div>

      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">Categories</h4>
        <div class="flex flex-wrap gap-1">
          @for (cat of skills.categories(); track cat) {
          <span class="px-2 py-0.5 bg-surface-variant rounded text-[10px]">{{ cat }}</span>
          }
        </div>
      </div>

      <div class="space-y-2">
        <h4 class="text-xs font-medium-variant">Sample skills (first 8)</h4>
        <div class="p-2 bg-surface-variant rounded max-h-32 overflow-y-auto space-y-1 text-[10px]">
          @for (skill of skills.skills().slice(0, 8); track skill.id) {
          <div class="flex justify-between gap-2">
            <span class="font-medium truncate" [title]="skill.name">{{ skill.name }}</span>
            <span class="text-on-surface-variant shrink-0">{{ skill.area }}</span>
          </div>
          }
        </div>
      </div>

      <details class="text-xs">
        <summary
          class="cursor-pointer font-medium-variant hover:text-on-surface p-2 bg-surface-variant rounded">
          View all skills ({{ skills.skills().length }})
        </summary>
        <pre class="p-2 bg-surface rounded text-[10px] overflow-x-auto max-h-60 overflow-y-auto">{{
          skills.skills() | json
        }}</pre>
      </details>
    </div>
    }
  </div>
</div>